import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { I18nProvider, useI18n } from "@/lib/i18n";
import { AUTHOR_LINE } from "@/lib/document-credits";

import { track } from "@/lib/analytics";
import { reportWalletFailure } from "@/lib/reliability";
import { attributionProps, getPurchaseAttribution } from "@/lib/purchase-attribution";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { TurnstileWidget, captchaEnabled } from "@/components/TurnstileWidget";
import { QRCodeSVG } from "qrcode.react";
import { solscanAccountUrl } from "@/lib/solana-rpc";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SocialIcons } from "@/components/SocialIcons";

import { EcosystemSwitcher, EcosystemHub } from "@/components/EcosystemHub";
import { useReveal, useTilt } from "@/hooks/use-fx";
import { useModalA11y } from "@/hooks/use-modal-a11y";
import { getInjectedProvider, useWallet } from "@/hooks/use-wallet";
import { SFN_TREASURY_ADDRESS, shortTreasury } from "@/lib/ecosystem-constants";
import {
  TreasuryTransparency,
  PurchaseConfirmModal,
  useTreasuryGuard,
  NetworkStatusNote,
} from "@/components/TreasuryTransparency";
import { buildPurchaseReceipt, explorerTxUrl, type PurchaseReceipt } from "@/lib/treasury";
import { TxTroubleshootTrigger } from "@/components/TxTroubleshootModal";
import { useSolPrice, solToUsd } from "@/lib/price-feed";
import {
  buildQuote,
  checkQuote,
  secondsLeft,
  clampSlippage,
  clampTtl,
  SLIPPAGE_OPTIONS,
  QUOTE_TTL_OPTIONS,
  DEFAULT_SLIPPAGE_PCT,
  DEFAULT_QUOTE_TTL_S,
} from "@/lib/swap-quote";
import {
  DEX_BADGES,
  DEFAULT_PHASE_STATUS,
  PHASE_IDS,
  parsePhaseStatus,
  type PhaseId,
  type PhaseStatus,
} from "@/lib/dex-roadmap";
import { VerifyReceiptSection } from "@/components/VerifyReceipt";
import { classifyWalletError, sendTreasuryPayment } from "@/lib/solana-pay";
import { logSale } from "@/lib/sales-log";
import { WidgetErrorBoundary } from "@/components/WidgetErrorBoundary";
import { AnalyticsConsentBar } from "@/components/AnalyticsConsentBar";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Coins,
  MapPin,
  ShieldCheck,
  Sparkles,
  Activity,
  Building2,
  Utensils,
  Users,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Droplets,
  Flame,
  Gift,
  Vault,
  ChefHat,
  QrCode,
  Trophy,
  HandHeart,
  FileText,
  Map,
  Wheat,
  Sun,
  Sparkle,
  Lock,
  ExternalLink,
  Factory,
  GitBranch,
  Target,
  Download,
  Loader,
  RefreshCw,
  Receipt,
  Banknote,
  ScanLine,
  Wallet,
  CreditCard,
  X,
  Info,
  Globe,
  Menu,
  Sprout,
  Store,
  Cpu,
} from "lucide-react";
import storyKitchen from "@/assets/story/kitchen.jpg.asset.json";
import storyFarmer from "@/assets/story/farmer.jpg.asset.json";
import storyDiners from "@/assets/story/diners.jpg.asset.json";
import storyPilot from "@/assets/story/pilot-region.jpg.asset.json";
import { ImpactLoop, BrandSynergy, PhaseOneCard, WhyOnChain } from "@/components/ImpactStory";
import { LegalDocModal } from "@/components/legal/LegalDocModal";
import type { DocId } from "@/components/legal/legal-documents";


import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  VillagePandoEcosystem,
  EcosystemAccentProvider,
  useEcosystemAccent,
} from "@/components/VillagePandoEcosystem";
import { fetchMintInfo } from "@/lib/solana-rpc";
import meal4CoinAsset from "@/assets/meal4-token.png.asset.json";
import meal2CoinAsset from "@/assets/meal2-token.png.asset.json";
const meal4Coin = meal4CoinAsset.url;
const meal2Coin = meal2CoinAsset.url;
import jupiterAsset from "@/assets/dex/jupiter.png.asset.json";
import raydiumAsset from "@/assets/dex/raydium.png.asset.json";
import coingeckoAsset from "@/assets/dex/coingecko.png.asset.json";
import coinmarketcapAsset from "@/assets/dex/coinmarketcap.svg.asset.json";
const jupiterLogo = jupiterAsset.url;
const raydiumLogo = raydiumAsset.url;
const coingeckoLogo = coingeckoAsset.url;
const coinmarketcapLogo = coinmarketcapAsset.url;
import ingredients from "@/assets/floating-ingredients.png";

/* ---------------- NAV ---------------- */
type NavLink = { href: string; labelKey: string; id: string; external?: boolean };
const NAV_LINKS: NavLink[] = [
  { href: "#tokens", labelKey: "nav.product", id: "nav_product" },
  { href: "#pilot", labelKey: "nav.pilot", id: "nav_pilot" },
  { href: "#tokenomics", labelKey: "nav.tokenomics", id: "nav_tokenomics" },
  { href: "/vision", labelKey: "nav.vision", id: "nav_vision" },
  { href: "/whitepaper", labelKey: "nav.whitepaper", id: "nav_whitepaper" },
];


function Nav() {
  const { t } = useI18n();
  const { accent } = useEcosystemAccent();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div
        className="w-full mx-0 mt-4 grid grid-cols-[minmax(0,auto)_minmax(0,1fr)_minmax(0,auto)] items-center gap-4 rounded-full glass-strong px-6 py-2.5 lg:gap-6 lg:px-8 lg:py-3 transition-shadow duration-500"
        style={{
          boxShadow:
            accent.id === "default"
              ? undefined
              : `0 0 0 1px ${accent.color.replace(")", " / 0.35)")}, 0 12px 40px ${accent.color.replace(")", " / 0.18)")}`,
        }}
      >
        {/* LEFT — dual-token brand */}
        <a href="#top" className="flex min-w-0 items-center gap-2.5 whitespace-nowrap">
          <span className="relative flex h-9 w-[52px] shrink-0 items-center" aria-hidden>
            <img
              src={meal2Coin}
              alt=""
              width={36}
              height={36}
              decoding="async"
              className="absolute left-0 top-0 h-9 w-9 rounded-full object-contain drop-shadow-[0_0_10px_rgba(52,211,153,0.45)]"
            />
            <img
              src={meal4Coin}
              alt=""
              width={36}
              height={36}
              decoding="async"
              className="absolute left-4 top-0 h-9 w-9 rounded-full object-contain drop-shadow-[0_0_10px_rgba(234,179,8,0.5)]"
            />
          </span>
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="font-display text-sm font-semibold tracking-tight">MealToken24</span>
            <span className="hidden text-[10px] font-normal uppercase tracking-[0.16em] text-muted-foreground sm:inline">
              {t("brand.tagline")}
            </span>
          </span>
        </a>


        {/* CENTER — primary nav (max 5) */}
        <nav className="hidden min-w-0 items-center justify-center gap-x-6 whitespace-nowrap text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground xl:flex 2xl:gap-x-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={() => track("cta_click", { id: l.id, surface: "nav" })}
              className="transition-colors hover:text-foreground"
            >
              {t(l.labelKey)}
            </a>
          ))}
        </nav>

        {/* RIGHT — action cluster */}
        <div className="flex items-center justify-end gap-3 whitespace-nowrap">
          <LiveStatusIndicator />
          <span className="hidden sm:inline-flex">
            <LanguageSwitcher />
          </span>
          <NavConnectButton />
          <button
            type="button"
            aria-label="Open navigation menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/10 sm:h-9 sm:w-9 bg-white/[0.04] text-foreground/80 transition-colors hover:bg-white/[0.08] xl:hidden"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>
      </div>

      <MobileNavSheet open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}

function LiveStatusIndicator() {
  return (
    <span
      className="hidden items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300 whitespace-nowrap md:inline-flex"
      aria-label="Network status: live"
    >
      <span className="relative grid h-1.5 w-1.5 place-items-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/70" />
        <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-400" />
      </span>
      Live
    </span>
  );
}

function MobileNavSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="Mobile navigation">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col gap-6 border-l border-white/10 bg-[oklch(0.10_0.012_260_/_0.97)] p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <span className="font-display text-base font-bold tracking-tight">Menu</span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-foreground/80 hover:bg-white/[0.08]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex flex-col gap-1 text-sm">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={() => {
                track("cta_click", { id: l.id, surface: "nav_mobile" });
                onClose();
              }}
              className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 font-medium text-foreground/90 transition-colors hover:border-white/10 hover:bg-white/[0.05]"
            >
              {t(l.labelKey)}
            </a>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-4 border-t border-white/5 pt-4">
          <SocialIcons surface="nav_mobile" />
          <div className="flex items-center justify-between">
            <LanguageSwitcher />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("nav.connectWallet")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavConnectButton() {
  const { t } = useI18n();
  const {
    connected,
    address,
    providerLabel,
    clusterLabel,
    cluster,
    clusterMismatch,
    connecting,
    disconnecting,
    openConnect,
    disconnect,
  } = useWallet();
  const busy = connecting || disconnecting;
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Real on-chain public key, truncated for display (e.g. H1yR…av6z).
  const short = address ? `${address.slice(0, 4)}…${address.slice(-4)}` : "";

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!connected) setMenuOpen(false);
  }, [connected]);

  const handleClick = () => {
    track("cta_click", { id: "nav_connect_wallet", surface: "nav" });
    if (connected) setMenuOpen((v) => !v);
    else openConnect();
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-busy={busy}
        aria-haspopup={connected ? "menu" : undefined}
        aria-expanded={connected ? menuOpen : undefined}
        title={connected ? (address ?? undefined) : undefined}
        className={cn(
          "liquid-btn inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-xs sm:min-h-0 font-bold uppercase tracking-wide shadow-[var(--glow-saffron)] transition-all",
          busy && "cursor-not-allowed opacity-70",
          connected
            ? "border border-emerald-400/40 bg-emerald-400/10 font-mono text-emerald-300"
            : "bg-gradient-to-br from-saffron to-coral text-obsidian",
        )}
      >
        {busy ? (
          <>
            <Loader2 aria-hidden className="h-3.5 w-3.5 shrink-0 animate-spin" />
            {connecting ? "Connecting…" : "Disconnecting…"}
          </>
        ) : connected ? (
          <>
            <span
              aria-hidden
              className={cn(
                "h-1.5 w-1.5 shrink-0 animate-pulse rounded-full",
                clusterMismatch
                  ? "bg-amber-400 shadow-[0_0_8px_#F59E0B]"
                  : "bg-emerald-400 shadow-[0_0_8px_#10B981]",
              )}
            />
            <span className="sr-only">Connected · </span>
            {providerLabel ? `${providerLabel} • ${short}` : short}
          </>
        ) : (
          t("nav.connectWallet")
        )}
      </button>

      {connected && menuOpen && (
        <div
          role="menu"
          className="absolute right-0 z-[120] mt-2 w-64 rounded-2xl border border-white/10 bg-[oklch(0.10_0.012_260_/_0.97)] p-3 shadow-[0_20px_60px_rgba(0,0,0,0.6)] backdrop-blur-md"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-300">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#10B981]" />
              {providerLabel ? `${providerLabel} · connected` : "Connected"}
            </span>
            <span
              className={cn(
                "font-mono text-[10px] uppercase tracking-[0.14em]",
                clusterMismatch ? "text-amber-300" : "text-muted-foreground",
              )}
            >
              {clusterLabel}
            </span>
          </div>

          {address && (
            <div className="mt-3 flex justify-center">
              <div className="rounded-xl bg-white p-2">
                <QRCodeSVG value={address} size={116} level="M" marginSize={0} />
              </div>
            </div>
          )}
          <p className="mt-2 text-center text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            Scan to send to this wallet
          </p>

          <div className="mt-1 break-all font-mono text-[11px] text-foreground">{address}</div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              if (!address) return;
              void navigator.clipboard
                ?.writeText(address)
                .then(() =>
                  toast.success("Address copied to clipboard", { description: short }),
                )
                .catch(() =>
                  toast.error("Could not copy address", {
                    description: "Copy it manually from the wallet menu.",
                  }),
                );
            }}
            className="mt-3 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-[11px] font-semibold text-foreground hover:bg-white/[0.08]"
          >
            Copy address
          </button>
          {address && (
            <a
              role="menuitem"
              href={solscanAccountUrl(address, cluster)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-[11px] font-semibold text-foreground hover:bg-white/[0.08]"
            >
              View on Solscan
              <ExternalLink aria-hidden className="h-3 w-3" />
            </a>
          )}
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => {
              setMenuOpen(false);
              disconnect();
              track("cta_click", { id: "nav_disconnect_wallet", surface: "nav" });
            }}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2 text-[11px] font-semibold text-red-300 hover:bg-red-400/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {disconnecting && <Loader2 aria-hidden className="h-3 w-3 animate-spin" />}
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => {
              setMenuOpen(false);
              disconnect();
              openConnect();
            }}
            className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-[11px] font-semibold text-foreground hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Switch wallet
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- ABOUT ---------------- */
function About() {
  const { t } = useI18n();
  const ref = useReveal<HTMLDivElement>();
  return (
    <section id="about" className="relative py-24 sm:py-32">
      <div ref={ref} className="reveal mx-auto max-w-5xl px-4 sm:px-6 text-center">
        <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          <Users className="h-3 w-3 text-mint" />
          {t("about.eyebrow")}
        </div>
        <h2 className="mt-6 font-display text-[clamp(2rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-tight">
          {t("about.titleA")}{" "}
          <span className="italic text-gradient-saffron">{t("about.titleB")}</span>
        </h2>
        <p className="mx-auto mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {t("about.body")}
        </p>
      </div>
    </section>
  );
}

/* ---------------- CONTACT ---------------- */
function Contact() {
  const { t, lang } = useI18n();
  const ref = useReveal<HTMLDivElement>();
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "sending") return;
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    setStatus("sending");
    setErrorMsg(null);
    track("cta_click", { id: "contact_submit", surface: "contact" });

    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          subject: String(fd.get("subject") ?? ""),
          message: String(fd.get("message") ?? ""),
          website: String(fd.get("website") ?? ""), // honeypot
          captchaToken,
          lang,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (!res.ok || !data.ok) {
        const msg = data?.error ?? "Submission failed. Please try again.";
        setErrorMsg(msg);
        setStatus("error");
        toast.error(msg);
        return;
      }
      setStatus("ok");
      formEl.reset();
      setCaptchaToken(null);
      toast.success("Thank you! Your message has been sent successfully.");
    } catch {
      setErrorMsg("Network error. Please try again.");
      setStatus("error");
      toast.error("Network error. Please try again.");
    }
  };

  const dismissOverlay = () => {
    setStatus("idle");
    setErrorMsg(null);
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32">
      <div ref={ref} className="reveal mx-auto max-w-3xl px-4 sm:px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-mint glow-mint" />
            {t("contact.eyebrow")}
          </div>
          <h2 className="mt-6 font-display text-[clamp(2rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-tight">
            {t("contact.titleA")}{" "}
            <span className="italic text-gradient-saffron">{t("contact.titleB")}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
            {t("contact.body")}
          </p>
          <div className="mt-8 flex justify-center">
            <SocialIcons surface="contact" />
          </div>
        </div>

        <div className="relative mt-12">
          <form
            onSubmit={onSubmit}
            aria-busy={status === "sending"}
            className="grid gap-5 rounded-3xl glass-strong p-6 sm:p-8"
          >
            {/* Honeypot — invisible to humans, irresistible to bots */}
            <div className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden" aria-hidden="true">
              <label>
                Website
                <input type="text" name="website" tabIndex={-1} autoComplete="off" />
              </label>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t("contact.name")}
                </span>
                <input
                  required
                  maxLength={120}
                  type="text"
                  name="name"
                  autoComplete="name"
                  className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-saffron transition-colors"
                  placeholder={t("contact.namePh")}
                />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {t("contact.email")}
                </span>
                <input
                  required
                  maxLength={255}
                  type="email"
                  name="email"
                  autoComplete="email"
                  className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-saffron transition-colors"
                  placeholder={t("contact.emailPh")}
                />
              </label>
            </div>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {t("contact.subject")}
              </span>
              <input
                required
                maxLength={200}
                type="text"
                name="subject"
                className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-saffron transition-colors"
                placeholder={t("contact.subjectPh")}
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {t("contact.message")}
              </span>
              <textarea
                required
                maxLength={4000}
                name="message"
                rows={5}
                className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-saffron transition-colors resize-none"
                placeholder={t("contact.messagePh")}
              />
            </label>
            <TurnstileWidget onToken={setCaptchaToken} />
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-muted-foreground">
                {t("contact.responseNote")}
              </p>
              <button
                type="submit"
                disabled={status === "sending" || (captchaEnabled && !captchaToken)}
                className="liquid-btn inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-saffron to-coral px-7 py-3.5 text-sm font-bold tracking-wide text-obsidian shadow-[var(--glow-saffron)] disabled:opacity-70"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("contact.send")}
                  </>
                ) : (
                  <>
                    {t("contact.send")}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Success / Error overlay — neon-green dark glass */}
          {(status === "ok" || status === "error") && (
            <div
              role="status"
              aria-live="polite"
              className="absolute inset-0 z-10 flex items-center justify-center rounded-3xl bg-obsidian/85 p-6 backdrop-blur-md animate-in fade-in zoom-in-95 duration-300"
              style={{
                boxShadow:
                  status === "ok"
                    ? "inset 0 0 0 1px rgba(222,255,154,0.55), 0 0 60px rgba(222,255,154,0.18)"
                    : "inset 0 0 0 1px rgba(255,99,99,0.45), 0 0 60px rgba(255,99,99,0.15)",
              }}
            >
              <div className="max-w-md text-center">
                {status === "ok" ? (
                  <>
                    <div
                      className="mx-auto grid h-16 w-16 place-items-center rounded-full"
                      style={{
                        background:
                          "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.9), rgba(222,255,154,0.95) 50%, rgba(168,222,77,0.95) 100%)",
                        boxShadow: "0 0 40px rgba(222,255,154,0.7)",
                      }}
                    >
                      <CheckCircle2 className="h-7 w-7 text-obsidian" strokeWidth={2.5} />
                    </div>
                    <h3
                      className="mt-5 font-display text-2xl font-semibold tracking-tight"
                      style={{ color: "#DEFF9A" }}
                    >
                      {t("contact.sent")}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {t("contact.successBody")}
                    </p>
                  </>
                ) : (
                  <>
                    <div
                      className="mx-auto grid h-16 w-16 place-items-center rounded-full border"
                      style={{
                        borderColor: "rgba(255,120,120,0.6)",
                        background: "rgba(40,10,15,0.8)",
                        boxShadow: "0 0 30px rgba(255,99,99,0.45)",
                      }}
                    >
                      <AlertCircle className="h-7 w-7 text-coral" strokeWidth={2.5} />
                    </div>
                    <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight text-coral">
                      {t("contact.errorTitle")}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {errorMsg ?? t("contact.errorBody")}
                    </p>
                  </>
                )}
                <button
                  type="button"
                  onClick={dismissOverlay}
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80 transition-colors hover:bg-white/[0.08]"
                >
                  {status === "ok" ? t("contact.dismissOk") : t("contact.dismissError")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------------- HERO ---------------- */
/**
 * Large floating 3D coin art for the hero's left column.
 * The coin matching the purchase card currently in view is highlighted.
 */
function FloatingTokenArt() {
  const { t } = useI18n();
  const reducedMotion = usePrefersReducedMotion();
  const [active, setActive] = useState<"MEAL2" | "MEAL4">("MEAL4");
  const lastTracked = useRef<string | null>(null);

  // Report which coin visual holds attention (deduped per change).
  const highlight = useCallback((token: "MEAL2" | "MEAL4", source: "scroll" | "keyboard") => {
    setActive(token);
    const key = `${token}:${source}`;
    if (lastTracked.current === key) return;
    lastTracked.current = key;
    track("token_visual_highlight", { token, source });
  }, []);

  // Warm the coin bitmaps early so the hero art never pops in late on mobile.
  useEffect(() => {
    const links = [meal4Coin, meal2Coin].map((href) => {
      const l = document.createElement("link");
      l.rel = "preload";
      l.as = "image";
      l.href = href;
      document.head.appendChild(l);
      return l;
    });
    return () => links.forEach((l) => l.remove());
  }, []);

  useEffect(() => {
    const pick = () => {
      const cards = Array.from(document.querySelectorAll<HTMLElement>("[data-token-card]"));
      if (!cards.length) return;
      const focus = window.innerHeight * 0.45;
      let best: { token: string; d: number } | null = null;
      for (const c of cards) {
        const b = c.getBoundingClientRect();
        // Distance from the viewport focus line to the card's visible span.
        const d = Math.max(b.top - focus, focus - b.bottom, 0);
        const token = c.getAttribute("data-token-card") ?? "";
        if (!best || d < best.d) best = { token, d };
      }
      if (best?.token === "MEAL2" || best?.token === "MEAL4") highlight(best.token, "scroll");
    };
    pick();
    window.addEventListener("scroll", pick, { passive: true });
    window.addEventListener("resize", pick);
    return () => {
      window.removeEventListener("scroll", pick);
      window.removeEventListener("resize", pick);
    };
  }, [highlight]);


  const coins = [
    {
      token: "MEAL4" as const,
      src: meal4Coin,
      accent: "#DEFF9A",
      float: "float-slow",
      label: t("tokenart.meal4"),
    },
    {
      token: "MEAL2" as const,
      src: meal2Coin,
      accent: "#8FE3A8",
      float: "float-slower",
      label: t("tokenart.meal2"),
    },
  ];

  return (
    <div
      data-testid="floating-token-art"
      role="group"
      aria-label={t("tokenart.grouplabel")}
      className="relative mt-12 hidden lg:block"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-10 -z-10 rounded-[3rem] opacity-60 blur-3xl transition-all duration-700"
        style={{
          background:
            active === "MEAL4"
              ? "radial-gradient(circle at 30% 35%, oklch(0.92 0.16 110 / 0.22), transparent 62%)"
              : "radial-gradient(circle at 30% 35%, oklch(0.90 0.13 155 / 0.22), transparent 62%)",
        }}
      />
      <div className="flex flex-col gap-6">
        {coins.map((c, i) => {
          const on = active === c.token;
          return (
            <button
              key={c.token}
              type="button"
              data-active={on}
              aria-pressed={on}
              onFocus={() => highlight(c.token, "keyboard")}
              onClick={() => {
                highlight(c.token, "keyboard");
                document
                  .querySelector(`[data-token-card="${c.token}"]`)
                  ?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "center" });
              }}
              className={cn(
                "group flex w-full items-center gap-5 rounded-3xl border p-4 text-left transition-all duration-500",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                on
                  ? "border-white/15 bg-white/[0.04] shadow-[var(--shadow-elev)]"
                  : "border-white/[0.06] bg-white/[0.012] opacity-55",
              )}
              style={{ transform: on ? "translateY(-2px)" : undefined }}
            >
              <div
                className={cn("relative shrink-0", !reducedMotion && c.float)}
                style={reducedMotion ? undefined : { animationDelay: `${i * 1.4}s` }}
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -z-10 rounded-full blur-2xl transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle, ${c.accent}66, transparent 70%)`,
                    opacity: on ? 1 : 0.35,
                  }}
                />
                <img
                  src={c.src}
                  alt={c.label}
                  width={132}
                  height={132}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  sizes="(min-width: 1280px) 132px, 104px"
                  className="h-[104px] w-[104px] rounded-full object-contain transition-transform duration-500 xl:h-[132px] xl:w-[132px]"
                  style={{
                    filter: on
                      ? `drop-shadow(0 18px 34px ${c.accent}59)`
                      : "drop-shadow(0 10px 22px rgba(0,0,0,0.5)) saturate(0.7)",
                    transform: on ? "scale(1.04)" : "scale(0.96)",
                  }}
                />
              </div>
              <p
                className="min-w-0 text-sm leading-relaxed transition-colors duration-500"
                style={{ color: on ? c.accent : undefined }}
              >
                <span className={cn(!on && "text-muted-foreground")}>{c.label}</span>
              </p>
            </button>
          );
        })}
      </div>
      <p className="sr-only" aria-live="polite">
        {t("tokenart.highlighted").replace("{token}", active)}
      </p>
    </div>
  );
}

function DexRoadmap() {
  const { t } = useI18n();

  const logos: Record<string, { src: string; invert: boolean }> = {
    Jupiter: { src: jupiterLogo, invert: false },
    Raydium: { src: raydiumLogo, invert: false },
    CoinGecko: { src: coingeckoLogo, invert: false },
    CoinMarketCap: { src: coinmarketcapLogo, invert: true },
  };

  // Live phase status with graceful fallback: SSR and any fetch failure keep
  // the protocol defaults, so the card always renders a meaningful state.
  const [status, setStatus] = useState<Record<PhaseId, PhaseStatus>>(DEFAULT_PHASE_STATUS);
  const [statusLive, setStatusLive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/public/dex-status", { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { phases?: unknown };
        if (cancelled) return;
        setStatus(parsePhaseStatus(body?.phases));
        setStatusLive(true);
      } catch {
        /* keep DEFAULT_PHASE_STATUS — the card degrades silently */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-testid="dex-roadmap"
      onClick={() => track("dex_roadmap_click", { live: statusLive })}
      className="mt-8 hidden overflow-hidden rounded-3xl glass luminous-border p-6 shadow-[var(--shadow-elev)] lg:block"
    >
      <h3 className="font-display text-base font-semibold tracking-tight text-foreground/90">
        {t("dexRoadmap.title")}
      </h3>

      <div className="mt-5 flex items-center justify-between gap-3">
        {DEX_BADGES.map((b) => {
          const logo = logos[b.name];
          return (
            <a
              key={b.name}
              href={b.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`dex-badge-${b.name.toLowerCase()}`}
              aria-label={t("dexRoadmap.visit").replace("{name}", b.name)}
              title={t("dexRoadmap.visit").replace("{name}", b.name)}
              onClick={() => track("dex_badge_click", { badge: b.name })}
              className="rounded-lg outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-saffron"
            >
              <img
                src={logo?.src}
                alt={b.name}
                width={96}
                height={32}
                loading="lazy"
                decoding="async"
                className={cn(
                  "h-7 w-auto max-w-[88px] object-contain opacity-90 transition-opacity hover:opacity-100",
                  logo?.invert && "brightness-0 invert",
                )}
              />
            </a>
          );
        })}
      </div>

      <ol className="mt-6 space-y-3">
        {PHASE_IDS.map((id, i) => {
          const state = status[id];
          const isActive = state === "active";
          return (
            <li key={id} className="flex items-start gap-3">
              <span
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                  isActive ? "bg-saffron text-obsidian" : "bg-muted text-muted-foreground",
                )}
              >
                {i + 1}
              </span>
              <div className="flex-1">
                <p
                  className={cn(
                    "text-sm leading-snug",
                    isActive ? "font-medium text-foreground" : "text-muted-foreground",
                  )}
                >
                  {t(`dexRoadmap.${id}`)}
                </p>
                <span
                  data-testid={`dex-status-${id}`}
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider",
                    isActive ? "text-saffron" : "text-muted-foreground",
                  )}
                >
                  {t(`dexRoadmap.status.${state}`)}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
      <p className="sr-only" aria-live="polite">
        {statusLive ? t("dexRoadmap.statusLive") : t("dexRoadmap.statusFallback")}
      </p>
    </div>
  );
}


function Hero() {
  const { t } = useI18n();
  // Skeleton until hydration completes, so the fold paints instantly on mobile.
  const [widgetReady, setWidgetReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setWidgetReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (

    <section
      id="top"
      className="relative overflow-hidden pt-28 pb-20 sm:pt-36 sm:pb-28 md:pt-40 md:pb-32"
    >
      {/* Cyber ambient */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{ background: "var(--gradient-cyber)" }}
      />
      {/* Floating ingredients parallax — hidden on mobile to prevent overflow */}
      <img
        src={ingredients}
        alt=""
        aria-hidden
        width={420}
        height={420}
        loading="lazy"
        decoding="async"
        className="pointer-events-none absolute -left-24 top-32 w-[280px] opacity-20 float-slower mix-blend-screen sm:w-[360px] md:w-[420px] md:opacity-30"
      />
      <img
        src={ingredients}
        alt=""
        aria-hidden
        width={480}
        height={480}
        loading="lazy"
        decoding="async"
        className="pointer-events-none absolute -right-32 bottom-10 w-[320px] opacity-15 float-slow mix-blend-screen rotate-180 sm:w-[400px] md:w-[480px] md:opacity-25"
      />

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:items-start lg:gap-10">
        <div className="lg:col-span-6 min-w-0">

          <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-saffron glow-saffron shrink-0" />
            {t("hero.eyebrow")}
          </div>
          <h1 className="mt-6 font-display text-[clamp(2rem,5.5vw,3.25rem)] font-medium leading-[1.02] tracking-tight sm:mt-7 lg:text-[clamp(2.5rem,3.6vw,3.75rem)]">
            {t("hero.title")
              .split("\n")
              .map((line, i) => (
                <span key={i} className="block">
                  {i === 0 ? line : <span className="italic text-gradient-saffron">{line}</span>}
                </span>
              ))}
          </h1>

          {/* Sub-hero clarity badge */}
          <p className="mt-6 inline-flex max-w-2xl items-start gap-2.5 rounded-2xl glass px-4 py-3 text-[13px] leading-relaxed text-foreground/90 sm:rounded-full sm:px-5 sm:text-sm">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-saffron" aria-hidden />
            <span>{t("hero.badge")}</span>
          </p>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:mt-7 sm:text-lg">
            {t("hero.sub")}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:mt-9 sm:flex-row sm:flex-wrap sm:items-center">
            <a
              href="#secure"
              onClick={() => track("cta_click", { id: "hero_buy_meal2", surface: "hero" })}
              className="liquid-btn group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-saffron to-coral px-5 py-3.5 text-sm font-bold tracking-wide text-obsidian shadow-[var(--glow-saffron)] sm:px-7 sm:py-4"
            >
              {t("hero.cta")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 shrink-0" />
            </a>
            <a
              href="#tokens"
              onClick={() => track("cta_click", { id: "hero_get_meal4", surface: "hero" })}
              className="liquid-btn inline-flex items-center justify-center gap-2 rounded-full glass px-5 py-3.5 text-sm font-medium text-foreground sm:px-6 sm:py-4"
            >
              {t("hero.ghost")}
              <ArrowDown className="h-4 w-4 shrink-0" />
            </a>
          </div>


          {/* Stat strip */}
          <dl className="mt-10 grid max-w-xl grid-cols-3 gap-px overflow-hidden rounded-2xl glass sm:mt-12">
            {[
              { v: "128k+", l: t("hero.stat1") },
              { v: "100%", l: t("hero.stat2") },
              { v: "<1s", l: t("hero.stat3") },
            ].map((s) => (
              <div key={s.l} className="bg-background/30 px-3 py-3 text-center sm:px-6 sm:py-4">
                <dt className="font-mono text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
                  {s.v}
                </dt>
                <dd className="mt-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground sm:text-[10px] sm:tracking-[0.16em]">
                  {s.l}
                </dd>
              </div>
            ))}
          </dl>

          {/* Floating 3D token art — fills the left column beside the widgets */}
          <FloatingTokenArt />
          {/* Targeted DEX listing & indexing roadmap */}
          <DexRoadmap />
        </div>


        {/* RIGHT — dual-token purchase module, above the fold */}
        <div className="relative lg:col-span-6 min-w-0">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-6 -z-10 rounded-[2.5rem] opacity-40 blur-3xl"
            style={{
              background:
                "radial-gradient(circle at 30% 20%, oklch(0.82 0.18 75 / 0.28), transparent 60%), radial-gradient(circle at 80% 80%, oklch(0.96 0.07 155 / 0.22), transparent 60%)",
            }}
          />
          <WidgetErrorBoundary surface="swap_widget">
            {widgetReady ? <TokenSale compact /> : <BuyWidgetSkeleton />}
          </WidgetErrorBoundary>

        </div>

      </div>
    </section>
  );
}

/* ---------------- NEXT MILESTONES ---------------- */
function NextMilestones() {
  const { t } = useI18n();
  const ref = useReveal<HTMLDivElement>();
  return (
    <section id="milestones" className="relative overflow-hidden py-20 sm:py-28">
      {/* Subtle green ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(70% 50% at 50% 0%, oklch(0.96 0.07 155 / 0.10), transparent 60%)," +
            "radial-gradient(50% 60% at 80% 100%, oklch(0.96 0.07 155 / 0.06), transparent 60%)",
        }}
      />

      <div ref={ref} className="reveal mx-auto max-w-7xl px-4 sm:px-6">
        {/* Subtitle */}
        <div className="text-center">
          <span className="inline-block text-[11px] font-medium uppercase tracking-[0.22em] text-mint">
            // {t("milestones.eyebrow")}
          </span>
        </div>

        {/* Pillars */}
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {/* Pillar 1 */}
          <div className="relative overflow-hidden rounded-3xl glass-strong luminous-border p-8 sm:p-10">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full blur-3xl opacity-50"
              style={{ background: "oklch(0.96 0.07 155 / 0.18)" }}
            />
            <div className="relative z-10 flex flex-col h-full">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-mint/10 text-mint ring-1 ring-mint/20">
                  <Factory className="h-5 w-5" />
                </div>
                <h2 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">
                  {t("milestones.p1.title")}
                </h2>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                {t("milestones.p1.desc")}
              </p>
              <div className="mt-auto pt-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-mint/30 bg-mint/[0.06] px-4 py-2 text-xs font-semibold text-mint shadow-[0_0_20px_rgba(168,222,77,0.12)]">
                  <Target className="h-3.5 w-3.5" />
                  {t("milestones.p1.target")}
                </div>
              </div>
            </div>
          </div>

          {/* Pillar 2 */}
          <div className="relative overflow-hidden rounded-3xl glass-strong luminous-border p-8 sm:p-10">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full blur-3xl opacity-50"
              style={{ background: "oklch(0.96 0.07 155 / 0.18)" }}
            />
            <div className="relative z-10 flex flex-col h-full">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-mint/10 text-mint ring-1 ring-mint/20">
                  <GitBranch className="h-5 w-5" />
                </div>
                <h2 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">
                  {t("milestones.p2.title")}
                </h2>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                {t("milestones.p2.desc")}
              </p>
              <div className="mt-auto pt-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-mint/30 bg-mint/[0.06] px-4 py-2 text-xs font-semibold text-mint shadow-[0_0_20px_rgba(168,222,77,0.12)]">
                  <Target className="h-3.5 w-3.5" />
                  {t("milestones.p2.target")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- BENTO ---------------- */
/* ---------------- ECOSYSTEM ARCHITECTURE FLOW ---------------- */
const ARCH_NODES = [
  { key: "n1", icon: Coins, tone: "saffron" },
  { key: "n2", icon: Store, tone: "mint" },
  { key: "n3", icon: Sprout, tone: "mint" },
  { key: "n4", icon: Wallet, tone: "saffron" },
  { key: "n5", icon: Cpu, tone: "mint" },
] as const;

function EcosystemArchitecture() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  const [active, setActive] = useState<string>("n1");

  return (
    <div ref={r} className="reveal mt-16">
      <div className="max-w-2xl">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mint">
          // {t("arch.eyebrow")}
        </span>
        <h3 className="mt-3 font-display text-2xl font-medium leading-[1.1] tracking-tight sm:text-3xl md:text-4xl">
          {t("arch.title")}
        </h3>
        <p className="mt-3 text-sm text-muted-foreground sm:text-base">{t("arch.sub")}</p>
      </div>

      <ol className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:gap-2">
        {ARCH_NODES.map((n, i) => {
          const isActive = active === n.key;
          const color = n.tone === "saffron" ? "oklch(0.82 0.18 75)" : "oklch(0.86 0.16 165)";
          return (
            <li key={n.key} className="relative flex items-stretch">
              <button
                type="button"
                onMouseEnter={() => setActive(n.key)}
                onFocus={() => setActive(n.key)}
                onClick={() => {
                  setActive(n.key);
                  track("cta_click", { id: `arch_node_${n.key}`, surface: "ecosystem" });
                }}
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "group flex w-full flex-col rounded-2xl glass p-4 text-left transition-all duration-300",
                  isActive ? "scale-[1.02]" : "opacity-80 hover:opacity-100",
                )}
                style={
                  isActive
                    ? { boxShadow: `0 0 0 1px ${color}, 0 12px 40px -18px ${color}` }
                    : undefined
                }
              >
                <div className="flex items-center justify-between">
                  <span
                    className="grid h-9 w-9 place-items-center rounded-xl"
                    style={{ background: `color-mix(in oklab, ${color} 18%, transparent)`, color }}
                  >
                    <n.icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.18em] text-muted-foreground">
                    0{i + 1}
                  </span>
                </div>
                <span className="mt-3 block text-sm font-semibold tracking-tight text-foreground">
                  {t(`arch.${n.key}.t`)}
                </span>
                <span className="mt-1.5 block text-xs leading-relaxed text-muted-foreground">
                  {t(`arch.${n.key}.d`)}
                </span>
              </button>
              {i < ARCH_NODES.length - 1 && (
                <ArrowRight
                  aria-hidden
                  className="absolute -right-3 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-muted-foreground lg:block"
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ---------------- PILOT / PROOF-OF-CONCEPT TRUST BANNER ---------------- */
function PilotTrustBanner() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  return (
    <section id="pilot" className="relative py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div
          ref={r}
          className="reveal relative grid overflow-hidden rounded-3xl glass-strong luminous-border md:grid-cols-[1fr_1.1fr]"
        >
          <img
            src={storyPilot.url}
            alt={t("story.pilot.alt")}
            width={1280}
            height={720}
            loading="lazy"
            decoding="async"
            className="h-52 w-full object-cover md:h-full"
          />
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 text-mint">
              <MapPin className="h-4 w-4" aria-hidden />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                {t("trustpilot.eyebrow")}
              </span>
            </div>
            <h3 className="mt-3 font-display text-2xl font-medium leading-[1.15] tracking-tight sm:text-3xl">
              {t("trustpilot.title")}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {t("trustpilot.desc")}
            </p>
            <ul className="mt-5 flex flex-wrap gap-2">
              {[
                t("trustpilot.b1"),
                t("trustpilot.b2"),
                t("trustpilot.b3"),
                t("trustpilot.b4"),
              ].map((b) => (
                <li
                  key={b}
                  className="inline-flex items-center gap-1.5 rounded-full glass px-3 py-1.5 text-[11px] text-foreground/85"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-mint" aria-hidden />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bento() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  return (
    <section id="ecosystem" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div ref={r} className="reveal max-w-2xl">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            // Sustainable Food & Eco Network
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("bento.title")}
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">{t("bento.sub")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              icon: Users,
              tone: "text-saffron",
              glow: "oklch(0.82 0.18 75 / 0.35)",
              img: storyKitchen.url,
              alt: t("story.kitchen.alt"),
              title: t("bento.card1.title"),
              text: t("bento.card1.text"),
              bullets: [t("bento.card1.b1"), t("bento.card1.b2"), t("bento.card1.b3")],
            },
            {
              icon: ChefHat,
              tone: "text-mint",
              glow: "oklch(0.86 0.16 165 / 0.35)",
              img: storyFarmer.url,
              alt: t("story.farmer.alt"),
              title: t("bento.card2.title"),
              text: t("bento.card2.text"),
              bullets: [t("bento.card2.b1"), t("bento.card2.b2"), t("bento.card2.b3")],
            },
            {
              icon: Building2,
              tone: "text-saffron",
              glow: "oklch(0.82 0.18 75 / 0.35)",
              img: storyDiners.url,
              alt: t("story.diners.alt"),
              title: t("bento.card3.title"),
              text: t("bento.card3.text"),
              bullets: [t("bento.card3.b1"), t("bento.card3.b2"), t("bento.card3.b3")],
            },
          ].map((c) => (
            <BentoTile key={c.title} className="relative overflow-hidden">
              <div className="relative z-10 flex h-full flex-col">
                <div className="relative -mx-1 mb-5 overflow-hidden rounded-2xl">
                  <img
                    src={c.img}
                    alt={c.alt}
                    width={1024}
                    height={768}
                    loading="lazy"
                    decoding="async"
                    className="h-40 w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-44"
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 35%, oklch(0.16 0.02 260 / 0.75) 100%)",
                    }}
                  />
                </div>
                <div className={cn("flex items-center gap-2", c.tone)}>
                  <c.icon className="h-4 w-4" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em]">
                    {c.title}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
                <ul className="mt-4 space-y-2">
                  {c.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13px] text-foreground/85">
                      <CheckCircle2
                        className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", c.tone)}
                        aria-hidden
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-10 h-64 w-64 rounded-full blur-3xl opacity-50"
                style={{ background: c.glow }}
              />
            </BentoTile>
          ))}
        </div>

        

        <PilotSandboxCard />
      </div>
    </section>
  );
}

function PilotSandboxCard() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  return (
    <div
      ref={r}
      className="reveal mt-10 relative overflow-hidden rounded-3xl glass-strong luminous-border p-6 sm:p-10"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full blur-3xl opacity-40"
        style={{ background: "oklch(0.82 0.18 75 / 0.55)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -bottom-24 h-80 w-80 rounded-full blur-3xl opacity-30"
        style={{ background: "oklch(0.86 0.16 165 / 0.45)" }}
      />

      <div className="relative grid gap-8 md:grid-cols-[1.2fr_1fr] md:items-center">
        <div>
          <div className="flex items-center gap-2 text-saffron">
            <MapPin className="h-4 w-4" aria-hidden />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
              // {t("pilot.eyebrow")}
            </span>
          </div>
          <h3 className="mt-3 font-display text-2xl font-medium leading-[1.1] tracking-tight sm:text-3xl md:text-4xl">
            {t("pilot.title")}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("pilot.body")}
          </p>

          {/* Phase badges */}
          <div
            className="mt-6 flex flex-wrap items-center gap-3"
            aria-label={t("pilot.phase.arrow")}
          >
            <span
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-obsidian"
              style={{
                background: "var(--gradient-saffron)",
                boxShadow: "var(--glow-saffron)",
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-obsidian/70" aria-hidden />
              {t("pilot.phase1")}
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span className="inline-flex items-center gap-2 rounded-full glass px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-foreground/85">
              <Globe className="h-3.5 w-3.5 text-mint" aria-hidden />
              {t("pilot.phase2")}
            </span>
          </div>
        </div>

        {/* Benchmark panel */}
        <div className="relative rounded-2xl glass p-5 sm:p-6">
          <div className="flex items-center gap-2 text-mint">
            <Globe className="h-4 w-4" aria-hidden />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
              {t("pilot.benchmark.label")}
            </span>
          </div>
          <div
            className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-4xl"
            style={{ color: "oklch(0.86 0.16 165)" }}
          >
            {t("pilot.benchmark.value")}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
            {t("pilot.benchmark.desc")}
          </p>

          <dl className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {[
              { l: t("pilot.stat1.label"), v: t("pilot.stat1.value") },
              { l: t("pilot.stat2.label"), v: t("pilot.stat2.value") },
              { l: t("pilot.stat3.label"), v: t("pilot.stat3.value") },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-white/[0.03] p-3">
                <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {s.l}
                </dt>
                <dd className="mt-1 text-sm font-medium tracking-tight text-foreground">{s.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

function BentoTile({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useTilt<HTMLDivElement>(4);
  return (
    <div
      ref={ref}
      className={`tilt-card group relative flex flex-col rounded-3xl glass luminous-border p-6 transition-shadow hover:shadow-[var(--shadow-elev)] sm:p-7 ${className}`}
    >
      {children}
    </div>
  );
}

/* ---------------- DUAL TOKEN ---------------- */
function DualToken() {
  const { t } = useI18n();
  const [active, setActive] = useState<"meal4" | "meal2">("meal4");
  const r = useReveal<HTMLDivElement>();
  const isMeal4 = active === "meal4";

  return (
    <section id="tokens" className="relative overflow-hidden py-24 sm:py-32">
      {/* Dynamic ambient that shifts with active token */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 transition-[background] duration-700"
        style={{
          background: isMeal4
            ? "radial-gradient(60% 50% at 30% 30%, oklch(0.82 0.18 75 / 0.22), transparent 65%), radial-gradient(50% 40% at 80% 70%, oklch(0.74 0.19 30 / 0.18), transparent 60%)"
            : "radial-gradient(60% 50% at 70% 30%, oklch(0.86 0.16 165 / 0.22), transparent 65%), radial-gradient(50% 40% at 20% 70%, oklch(0.6 0.15 200 / 0.16), transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div ref={r} className="reveal mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            // {t("dual.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("dual.title")}
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">{t("dual.sub")}</p>
        </div>

        {/* Toggle */}
        <div className="mt-10 flex justify-center">
          <div className="relative inline-flex items-center rounded-full glass p-1">
            <div
              aria-hidden
              className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full transition-transform duration-500 ease-out"
              style={{
                transform: isMeal4 ? "translateX(0)" : "translateX(100%)",
                background: isMeal4 ? "var(--gradient-saffron)" : "var(--gradient-mint)",
                boxShadow: isMeal4 ? "var(--glow-saffron)" : "var(--glow-mint)",
              }}
            />
            <button
              onClick={() => setActive("meal4")}
              className={`relative z-10 rounded-full px-6 py-2.5 text-sm font-bold transition-colors ${
                isMeal4 ? "text-obsidian" : "text-muted-foreground"
              }`}
            >
              Meal4
            </button>
            <button
              onClick={() => setActive("meal2")}
              className={`relative z-10 rounded-full px-6 py-2.5 text-sm font-bold transition-colors ${
                !isMeal4 ? "text-obsidian" : "text-muted-foreground"
              }`}
            >
              Meal2
            </button>
          </div>
        </div>

        {/* Cards + flow */}
        <div className="relative mt-14 grid gap-6 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
          <TokenCard
            kind="meal4"
            highlighted={isMeal4}
            image={meal4Coin}
            role={t("dual.meal4.role")}
            title={t("dual.meal4.t")}
            desc={t("dual.meal4.d")}
            bullets={[t("dual.meal4.b1"), t("dual.meal4.b2"), t("dual.meal4.b3")]}
          />

          {/* Energy flow */}
          <div className="relative hidden items-center justify-center md:flex">
            <svg viewBox="0 0 120 200" className="h-full w-32" aria-hidden>
              <defs>
                <linearGradient id="flow" x1="0" x2="1">
                  <stop offset="0%" stopColor="oklch(0.82 0.18 75)" />
                  <stop offset="100%" stopColor="oklch(0.86 0.16 165)" />
                </linearGradient>
              </defs>
              <path
                d="M10,40 C 60,40 60,160 110,160"
                fill="none"
                stroke="url(#flow)"
                strokeWidth="1.5"
                className="flow-line"
              />
              <path
                d="M10,160 C 60,160 60,40 110,40"
                fill="none"
                stroke="url(#flow)"
                strokeWidth="1.5"
                className="flow-line"
                style={{ animationDirection: "reverse" }}
              />
              <circle cx="60" cy="100" r="3" fill="oklch(0.82 0.18 75)" className="pulse" />
            </svg>
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t("dual.flow")}
              </div>
            </div>
          </div>

          <TokenCard
            kind="meal2"
            highlighted={!isMeal4}
            image={meal2Coin}
            role={t("dual.meal2.role")}
            title={t("dual.meal2.t")}
            desc={t("dual.meal2.d")}
            bullets={[t("dual.meal2.b1"), t("dual.meal2.b2"), t("dual.meal2.b3")]}
          />
        </div>

        {/* MEAL2 Utility Trio */}
        <div className="mt-14 grid gap-4 sm:grid-cols-3">
          {[
            {
              icon: QrCode,
              title: "QR-Redemption",
              desc: "Guests scan a table QR, pay in MEAL2, and unlock a hot plate in seconds — no cash, no card terminals.",
            },
            {
              icon: Trophy,
              title: "Eat-to-Earn",
              desc: "Every verified meal mints loyalty rewards. Regular guests earn discounts, free sides, and tier upgrades on-chain.",
            },
            {
              icon: HandHeart,
              title: "Sponsorships",
              desc: "International supporters fund meals directly. Each MEAL2 sent abroad redeems as a transparent, traceable plate served locally.",
            },
          ].map((u) => (
            <div key={u.title} className="rounded-2xl glass luminous-border p-5">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-mint/10 border border-mint/30">
                <u.icon className="h-5 w-5 text-mint" strokeWidth={1.5} />
              </div>
              <h4 className="mt-4 font-display text-lg font-semibold tracking-tight">{u.title}</h4>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{u.desc}</p>
            </div>
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-xl text-center text-sm text-muted-foreground">
          {t("dual.flow.d")}
        </p>
      </div>
    </section>
  );
}

function TokenCard({
  kind,
  highlighted,
  image,
  role,
  title,
  desc,
  bullets,
}: {
  kind: "meal4" | "meal2";
  highlighted: boolean;
  image: string;
  role: string;
  title: string;
  desc: string;
  bullets: string[];
}) {
  const ref = useTilt<HTMLDivElement>(6);
  const isMeal4 = kind === "meal4";
  return (
    <div
      ref={ref}
      className={`tilt-card relative flex flex-col rounded-3xl p-7 transition-all duration-500 ${
        highlighted ? "scale-[1.01]" : "opacity-75"
      } ${isMeal4 ? "glass luminous-border" : "glass-strong luminous-border"}`}
      style={{
        boxShadow: highlighted ? (isMeal4 ? "var(--glow-saffron)" : "var(--glow-mint)") : undefined,
      }}
    >
      <div className="flex items-start justify-between">
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
            isMeal4 ? "text-saffron" : "text-mint"
          }`}
        >
          {role}
        </span>
        <img
          src={image}
          alt={title}
          width={160}
          height={160}
          loading="lazy"
          decoding="async"
          className={`-mt-6 -mr-4 h-40 w-40 object-contain drop-shadow-[0_0_28px_rgba(255,255,255,0.15)] ${
            isMeal4 ? "float-slow" : "float-slower"
          }`}
        />
      </div>
      <h3
        className={`mt-2 font-display text-5xl font-medium ${
          isMeal4 ? "text-gradient-saffron" : "text-gradient-mint"
        }`}
      >
        {title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{desc}</p>
      <ul className="mt-6 space-y-2.5">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-sm text-foreground/90">
            <CheckCircle2
              className={`mt-0.5 h-4 w-4 shrink-0 ${isMeal4 ? "text-saffron" : "text-mint"}`}
            />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- PROTECTION ---------------- */
function ProtectionSection() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();

  const features = [
    {
      icon: ShieldCheck,
      tone: "saffron" as const,
      title: t("protection.f1.title"),
      text: t("protection.f1.text"),
    },
    {
      icon: Vault,
      tone: "mint" as const,
      title: t("protection.f2.title"),
      text: t("protection.f2.text"),
    },
    {
      icon: Activity,
      tone: "coral" as const,
      title: t("protection.f3.title"),
      text: t("protection.f3.text"),
    },
  ];

  return (
    <section id="protection" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div ref={r} className="reveal mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            // {t("protection.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("protection.title")}
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">{t("protection.sub")}</p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {features.map((f, i) => {
            const Icon = f.icon;
            const color =
              f.tone === "saffron"
                ? "oklch(0.82 0.18 75)"
                : f.tone === "mint"
                  ? "oklch(0.86 0.16 165)"
                  : "oklch(0.74 0.19 30)";
            return (
              <div
                key={i}
                className="reveal group relative flex flex-col rounded-3xl glass luminous-border p-7 sm:p-8 transition-shadow hover:shadow-[var(--shadow-elev)]"
                style={{ animationDelay: `${(i % 3) * 120}ms` }}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  style={{
                    background: `radial-gradient(300px circle at 50% 0%, ${color.replace(")", " / 0.12)")}, transparent 60%)`,
                  }}
                />
                <div className="relative z-10">
                  <div
                    className="grid h-12 w-12 place-items-center rounded-2xl glass"
                    style={{
                      boxShadow: `inset 0 0 0 1px ${color.replace(")", " / 0.3)")}`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color }} />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-medium tracking-tight">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- TOKEN SALE — Dual-Purpose Acquisition ---------------- */

/** Brief "quoting" pulse whenever a swap input changes — simulates price-impact calc. */
function useQuoting(value: string, delay = 450, quoteId?: string): boolean {
  const [quoting, setQuoting] = useState(false);
  useEffect(() => {
    setQuoting(true);
    const id = setTimeout(() => {
      setQuoting(false);
      if (quoteId) track("quote_loaded", { id: quoteId, input: value });
    }, delay);
    return () => clearTimeout(id);
  }, [value, delay, quoteId]);
  return quoting;
}


/**
 * Real settlement flow:
 *   idle → confirm (armed) → approving (wallet popup) → confirming (network)
 *   → success → idle
 */
export type PurchasePhase = "idle" | "confirm" | "approving" | "confirming" | "success";

function useConfirmFlow(): {
  phase: PurchasePhase;
  setPhase: (p: PurchasePhase) => void;
  arm: () => void;
} {
  const [phase, setPhase] = useState<PurchasePhase>("idle");
  const arm = () => {
    setPhase("confirm");
    setTimeout(() => setPhase((p) => (p === "confirm" ? "idle" : p)), 8000);
  };
  return { phase, setPhase, arm };
}

/** Label shown inside the purchase button for each settlement phase. */
export function purchasePhaseKey(phase: PurchasePhase): string {
  if (phase === "approving") return "swap.tx.awaitingApproval";
  if (phase === "confirming") return "swap.tx.confirmingChain";
  return "swap.tx.success";
}

/** On-page settlement status shown under each purchase button. */
export type TxNotice = {
  token: "MEAL2" | "MEAL4";
  kind: "pending" | "success" | "error";
  message: string;
  detail?: string;
  signature?: string;
};

function TxStatusPanel({ notice, onDismiss }: { notice: TxNotice; onDismiss: () => void }) {
  const tone =
    notice.kind === "success"
      ? { border: "#DEFF9A55", bg: "#DEFF9A0D", color: "#DEFF9A" }
      : notice.kind === "error"
        ? { border: "oklch(0.72 0.19 25 / 0.45)", bg: "oklch(0.72 0.19 25 / 0.08)", color: "oklch(0.82 0.15 25)" }
        : { border: "#FFD66B55", bg: "#FFD66B0D", color: "#FFD66B" };
  const Icon =
    notice.kind === "success" ? CheckCircle2 : notice.kind === "error" ? AlertCircle : Loader2;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={`tx-status-${notice.token.toLowerCase()}`}
      className="mt-3 rounded-xl border px-3 py-2.5 text-xs leading-relaxed"
      style={{ borderColor: tone.border, background: tone.bg }}
    >
      <div className="flex items-start gap-2">
        <Icon
          className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", notice.kind === "pending" && "animate-spin")}
          style={{ color: tone.color }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold" style={{ color: tone.color }}>
            {notice.message}
          </p>
          {notice.detail && (
            <p className="mt-0.5 break-all text-muted-foreground">{notice.detail}</p>
          )}
          {notice.signature && (
            <a
              href={explorerTxUrl(notice.signature)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] underline-offset-2 hover:underline"
              style={{ color: tone.color }}
            >
              Solscan <ArrowUpRight className="h-3 w-3" />
            </a>
          )}
        </div>
        {notice.kind !== "pending" && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

/** Placeholder shown while the buy widget hydrates on the client. */
export function BuyWidgetSkeleton() {
  return (
    <div
      data-testid="buy-widget-skeleton"
      aria-hidden
      className="grid gap-6 rounded-3xl border border-white/10 bg-white/[0.015] p-6 sm:p-7"
    >
      <div className="h-5 w-32 rounded-full bg-white/10 animate-pulse" />
      <div className="h-8 w-48 rounded-md bg-white/10 animate-pulse" />
      <div className="h-20 rounded-xl bg-white/[0.06] animate-pulse" />
      <div className="h-20 rounded-xl bg-white/[0.06] animate-pulse" />
      <div className="h-11 rounded-xl bg-white/10 animate-pulse" />
    </div>
  );
}

export function TokenSale({ compact = false }: { compact?: boolean }) {
  const { t } = useI18n();

  // Deep-link preselect: /#buy-meal2 or /#buy-meal4 (used by the /vision CTAs)
  // scrolls the matching purchase card into view and focuses it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = /^#buy-(meal2|meal4)$/i.exec(window.location.hash);
    if (!m) return;
    const token = m[1].toUpperCase() as "MEAL2" | "MEAL4";
    const attribution = getPurchaseAttribution();
    track("purchase_preselected", {
      token,
      source: attribution?.source ?? "deeplink",
      ...attributionProps(token),
    });
    const id = window.setTimeout(() => {
      const card = document.querySelector<HTMLElement>(`[data-token-card="${token}"]`);
      if (!card) return;
      card.scrollIntoView({ behavior: "smooth", block: "center" });
      card.setAttribute("tabindex", "-1");
      card.focus({ preventScroll: true });
      card.classList.add("ring-2", "ring-saffron/70", "ring-offset-2", "ring-offset-background");
      window.setTimeout(
        () =>
          card.classList.remove(
            "ring-2",
            "ring-saffron/70",
            "ring-offset-2",
            "ring-offset-background",
          ),
        2600,
      );
    }, 350);
    return () => window.clearTimeout(id);
  }, []);

  const r = useReveal<HTMLDivElement>();

  const [meal2Tab, setMeal2Tab] = useState<"web3" | "cash">("web3");
  const [meal2PayAmount, setMeal2PayAmount] = useState("25");
  const [meal2PayCurrency, setMeal2PayCurrency] = useState<"USDC" | "SOL">("USDC");
  // Route protection for the SOL → MEAL2 market leg.
  const [meal2Slippage, setMeal2Slippage] = useState<number>(DEFAULT_SLIPPAGE_PCT);
  const [meal2QuoteTtl, setMeal2QuoteTtl] = useState<number>(DEFAULT_QUOTE_TTL_S);
  const [quoteTick, setQuoteTick] = useState(0);
  const [meal4PayAmount, setMeal4PayAmount] = useState("500");
  const [meal4PayCurrency, setMeal4PayCurrency] = useState<"USDC" | "SOL">("USDC");
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);

  // ----- Fixed conversion rates (prepared for Solana RPC binding) -----
  // MEAL4 protocol price: $0.10 / token. SOL reference: $150 (treasury oracle).
  const MEAL4_USDC_RATE = 10;       // 1 USDC -> 10 MEAL4
  const MEAL4_SOL_RATE = 1500;      // 1 SOL  -> 1500 MEAL4
  const MEAL2_USDC_RATE = 1;        // 1 USDC -> 1 MEAL2 (strictly locked, see daily-utility spec)

  // Live SOL/USD market feed (Jupiter, proxied server-side). Native SOL is
  // routed through the USD quote so MEAL2 stays strictly $1 per unit.
  const solPrice = useSolPrice();

  const meal2UsdValue = useMemo(() => {
    const n = parseFloat(meal2PayAmount);
    if (Number.isNaN(n) || n <= 0) return 0;
    return meal2PayCurrency === "SOL" ? solToUsd(n, solPrice.price) : n;
  }, [meal2PayAmount, meal2PayCurrency, solPrice.price]);

  // Signed, time-boxed route quote for the SOL leg. Rebuilt whenever the
  // market rate refreshes, the amount changes, or the user retunes the limits.
  const meal2Quote = useMemo(
    () =>
      buildQuote({
        payAmount: parseFloat(meal2PayAmount) || 0,
        solUsd: solPrice.price,
        slippagePct: meal2Slippage,
        ttlSeconds: meal2QuoteTtl,
        source: solPrice.source,
      }),
    [meal2PayAmount, solPrice.price, solPrice.source, solPrice.at, meal2Slippage, meal2QuoteTtl],
  );

  const meal2QuoteSeconds = useMemo(
    () => secondsLeft(meal2Quote, Date.now() + quoteTick * 0),
    [meal2Quote, quoteTick],
  );

  // 1 Hz countdown, only while a SOL quote is on screen.
  useEffect(() => {
    if (meal2PayCurrency !== "SOL") return;
    const id = window.setInterval(() => setQuoteTick((v) => v + 1), 1000);
    return () => window.clearInterval(id);
  }, [meal2PayCurrency]);

  // Auto re-quote before the countdown can reach zero: the user sees a short
  // "refreshing rate" state instead of an expired-quote error.
  // The in-flight guard lives in a ref, so the 1 Hz countdown re-running this
  // effect can never strand the flag on (the finally always clears it).
  const [meal2QuoteRefreshing, setMeal2QuoteRefreshing] = useState(false);
  const meal2RefreshInFlight = useRef(false);
  useEffect(() => {
    if (meal2PayCurrency !== "SOL") return;
    if (meal2QuoteSeconds > 6 || meal2RefreshInFlight.current) return;
    meal2RefreshInFlight.current = true;
    setMeal2QuoteRefreshing(true);
    track("quote_auto_refresh", { token: "MEAL2", trigger: "countdown" });
    void solPrice.refresh().finally(() => {
      meal2RefreshInFlight.current = false;
      setMeal2QuoteRefreshing(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meal2PayCurrency, meal2QuoteSeconds]);



  const meal2ReceiveAmount = useMemo(() => {
    if (meal2UsdValue <= 0) return "0.00";
    return (meal2UsdValue * MEAL2_USDC_RATE).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [meal2UsdValue]);
  const meal4ReceiveAmount = useMemo(() => {
    const n = parseFloat(meal4PayAmount);
    if (Number.isNaN(n) || n <= 0) return "0.00";
    const rate = meal4PayCurrency === "SOL" ? MEAL4_SOL_RATE : MEAL4_USDC_RATE;
    return (n * rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [meal4PayAmount, meal4PayCurrency]);
  const meal4Quoting = useQuoting(meal4PayAmount + ":" + meal4PayCurrency, 450, "meal4");
  const meal2Quoting =
    useQuoting(meal2PayAmount + ":" + meal2PayCurrency, 450, "meal2") ||
    (meal2PayCurrency === "SOL" && solPrice.loading);

  const meal4Flow = useConfirmFlow();
  const meal2Flow = useConfirmFlow();

  // ----- MEAL4 milestone state (simulated treasury-routed contributions) -----
  const MEAL4_MILESTONE_GOAL = 350000;
  const MEAL4_TREASURY_WALLET = SFN_TREASURY_ADDRESS;
  const [meal4Raised, setMeal4Raised] = useState(184_500);
  const wallet = useWallet();

  // Treasury guard — purchase buttons stay disabled until the canonical
  // destination is validated (structure + on-chain owner on the right cluster).
  const treasury = useTreasuryGuard();
  const [receipt, setReceipt] = useState<PurchaseReceipt | null>(null);
  const [notice, setNotice] = useState<TxNotice | null>(null);

  const busy = (p: PurchasePhase) => p === "approving" || p === "confirming";

  /** Executes a REAL on-chain transfer to the SFN treasury, then books it. */
  const runPurchase = useCallback(
    async (token: "MEAL2" | "MEAL4") => {
      const flow = token === "MEAL4" ? meal4Flow : meal2Flow;
      const currency: "USDC" | "SOL" = token === "MEAL4" ? meal4PayCurrency : meal2PayCurrency;
      const payAmount = token === "MEAL4" ? meal4PayAmount : meal2PayAmount;
      const receiveAmount = token === "MEAL4" ? meal4ReceiveAmount : meal2ReceiveAmount;
      const n = parseFloat(payAmount);
      if (Number.isNaN(n) || n <= 0) return;
      const fail = (message: string) => setNotice({ token, kind: "error", message });

      if (!wallet.connected || !wallet.address) {
        wallet.openConnect();
        return;
      }
      if (wallet.clusterMismatch) {
        toast.error(t("swap.tx.clusterMismatch"));
        fail(t("swap.tx.clusterMismatch"));
        track("swap_failed", { token, code: "cluster_mismatch", ...attributionProps(token) });
        return;
      }
      const injected = wallet.provider ? getInjectedProvider(wallet.provider) : null;
      if (!injected) {
        toast.error(t("swap.tx.walletMissing"));
        fail(t("swap.tx.walletMissing"));
        track("swap_failed", { token, code: "wallet_missing", ...attributionProps(token) });
        return;
      }

      // Route protection: a SOL → MEAL2 purchase may only be signed while the
      // quote is fresh and the live rate still clears the user's slippage
      // limit. Anything else is refused before the wallet ever opens.
      let quoteMeta: { slippagePct: number; quotedSolUsd: number; quotedAt: number } | undefined;
      if (token === "MEAL2" && currency === "SOL") {
        let live = solPrice.price;
        try {
          const res = await fetch("/api/public/sol-price", { headers: { Accept: "application/json" } });
          if (res.ok) {
            const body = (await res.json()) as { price?: number };
            if (Number.isFinite(body?.price) && (body.price as number) > 0) live = body.price as number;
          }
        } catch {
          track("price_feed_client_error", { token });
        }
        // A lapsed quote is never a dead end: re-quote at the live rate and
        // continue. Only a genuine slippage breach blocks the purchase.
        let effectiveQuote = meal2Quote;
        let verdict = checkQuote(effectiveQuote, live, Date.now(), 2000);
        if (!verdict.ok && verdict.reason === "expired") {
          setMeal2QuoteRefreshing(true);
          effectiveQuote = buildQuote({
            payAmount: parseFloat(meal2PayAmount) || 0,
            solUsd: live,
            slippagePct: meal2Slippage,
            ttlSeconds: meal2QuoteTtl,
            source: solPrice.source,
          });
          track("quote_auto_refresh", { token, trigger: "submit" });
          void solPrice.refresh();
          setMeal2QuoteRefreshing(false);
          setQuoteTick((v) => v + 1);
          verdict = checkQuote(effectiveQuote, live, Date.now(), 2000);
        }
        if (!verdict.ok) {
          const message =
            verdict.reason === "expired" ? t("swap.quote.expiredError") : t("swap.quote.slippageError");
          toast.error(message);
          fail(message);
          track("swap_failed", { token, code: `quote_${verdict.reason}`, ...attributionProps(token) });
          setQuoteTick((v) => v + 1);
          return;
        }
        quoteMeta = {
          slippagePct: clampSlippage(meal2Slippage),
          quotedSolUsd: effectiveQuote.solUsd,
          quotedAt: effectiveQuote.quotedAt,
        };
      }


      setNotice({
        token,
        kind: "pending",
        message: t("swap.tx.awaitingApproval"),
        detail: `${payAmount} ${currency} → ${receiveAmount} ${token}`,
      });

      try {
        const signature = await sendTreasuryPayment({
          wallet: injected as never,
          payer: wallet.address,
          destination: SFN_TREASURY_ADDRESS,
          currency,
          amount: n,
          onProgress: (p) => {
            if (p === "awaiting-approval") {
              flow.setPhase("approving");
              setNotice({
                token,
                kind: "pending",
                message: t("swap.tx.awaitingApproval"),
                detail: `${payAmount} ${currency} → ${receiveAmount} ${token}`,
              });
            } else if (p === "confirming") {
              flow.setPhase("confirming");
              setNotice({
                token,
                kind: "pending",
                message: t("swap.tx.confirmingChain"),
                detail: `${payAmount} ${currency} → ${receiveAmount} ${token}`,
              });
            }
          },
        });

        flow.setPhase("success");
        const r = buildPurchaseReceipt({
          token,
          payAmount,
          payCurrency: currency,
          receiveAmount,
          signature,
        });
        setReceipt(r);
        logSale(r, wallet.address, null, quoteMeta);
        setNotice({
          token,
          kind: "success",
          message: t("swap.tx.success"),
          detail: `${payAmount} ${currency} → ${receiveAmount} ${token}`,
          signature,
        });

        const received = Number(receiveAmount.replace(/,/g, "")) || 0;
        wallet.debit(currency, n);
        wallet.credit(token, received);
        if (token === "MEAL4") setMeal4Raised((prev) => Math.min(prev + n, MEAL4_MILESTONE_GOAL));

        track("swap_confirmed", {
          token,
          payAmount,
          payCurrency: currency,
          cluster: r.cluster,
          onchain: true,
          ...attributionProps(token),
        });
        toast.success(t("swap.tx.success"), {
          description: `${signature.slice(0, 8)}…${signature.slice(-8)}`,
          action: {
            label: "Solscan",
            onClick: () => window.open(explorerTxUrl(signature), "_blank", "noopener,noreferrer"),
          },
        });
        setTimeout(() => flow.setPhase("idle"), 2500);
      } catch (err) {
        const e = classifyWalletError(err);
        flow.setPhase("idle");
        const message =
          e.code === "rejected"
            ? t("swap.tx.rejected")
            : e.code === "insufficient"
              ? t("swap.tx.insufficient")
              : e.message;
        toast.error(message);
        fail(message);
        track("swap_failed", { token, code: e.code, ...attributionProps(token) });
        reportWalletFailure(e.code, { token, currency, ...attributionProps(token) });
      }
    },
    [meal4Flow, meal2Flow, meal4PayCurrency, meal2PayCurrency, meal4PayAmount, meal2PayAmount, meal4ReceiveAmount, meal2ReceiveAmount, meal2Quote, meal2Slippage, solPrice.price, wallet, t],
  );


  const meal4Pct = Math.min(100, Math.round((meal4Raised / MEAL4_MILESTONE_GOAL) * 100));
  const fmtUSDC = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

  return (
    <section
      id="secure"
      className={cn("relative", compact ? "overflow-visible" : "overflow-hidden py-24 sm:py-32")}
    >
      {!compact && (
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(55% 55% at 20% 20%, oklch(0.92 0.14 115 / 0.10), transparent 60%), radial-gradient(50% 50% at 85% 80%, oklch(0.92 0.10 155 / 0.10), transparent 60%)",
          }}
        />
      )}
      <div ref={r} className={cn("reveal", compact ? "w-full" : "mx-auto max-w-6xl px-4 sm:px-6")}>
        {!compact && (
          <div className="text-center">
            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
              {t("swap2.eyebrow")}
            </span>
            <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              {t("swap2.title")}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
              {t("swap2.sub")}
            </p>
          </div>
        )}

        <div
          id="swap"
          className={cn(
            "grid gap-6",
            compact ? "grid-cols-1" : "mt-12 md:grid-cols-2 md:items-stretch",
          )}
        >

          {/* ===== LEFT CARD — MEAL4 ===== */}
          <article data-token-card="MEAL4" className="relative flex flex-col rounded-3xl border border-white/10 bg-white/[0.015] p-6 sm:p-7">
            <header className="flex items-start justify-between gap-4">
              <div>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
                  style={{ borderColor: "#DEFF9A55", color: "#DEFF9A", background: "#DEFF9A0D" }}
                >
                  <ShieldCheck className="h-3 w-3" />
                  {t("meal4.badge")}
                </span>
                <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight">
                  {t("meal4.title")}
                </h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("meal4.subtitle")}
                </p>
              </div>
              <img src={meal4Coin} alt="" width={48} height={48} loading="lazy" decoding="async" className="h-12 w-12 shrink-0" />
            </header>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("meal4.price")}
                </div>
                <div className="mt-1 font-display text-sm font-semibold">{t("meal4.priceValue")}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("meal4.hardCap")}
                </div>
                <div className="mt-1 font-display text-sm font-semibold">
                  1,000,000{" "}
                  <span className="text-muted-foreground font-normal">{t("meal4.neverInflated")}</span>
                </div>
              </div>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {t("meal4.desc")}
            </p>

            {/* ===== Milestone progress (direct protocol purchase, not DEX) ===== */}
            <div
              className="mt-5 rounded-2xl border p-4"
              style={{ borderColor: "#DEFF9A33", background: "#DEFF9A06" }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.18em]"
                    style={{ color: "#DEFF9A" }}
                  >
                    {t("swap.meal4.milestoneLabel")}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t("swap.meal4.milestoneProgress")
                      .replace("{raised}", fmtUSDC(meal4Raised))
                      .replace("{goal}", fmtUSDC(MEAL4_MILESTONE_GOAL))}
                  </div>
                </div>
                <span
                  tabIndex={0}
                  role="button"
                  aria-label={t("swap.meal4.tooltipAria")}
                  className="group relative inline-flex shrink-0 cursor-help items-center justify-center rounded-full border border-white/10 bg-white/[0.04] p-1.5 text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute right-0 top-full z-20 mt-2 w-64 rounded-lg border border-white/10 bg-obsidian/95 p-3 text-left text-[11px] leading-relaxed text-muted-foreground opacity-0 shadow-xl backdrop-blur transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
                  >
                    {t("swap.meal4.tooltip")}
                  </span>
                </span>
              </div>
              <div
                className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/[0.06]"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={meal4Pct}
                aria-label={t("swap.meal4.milestoneLabel")}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out"
                  style={{
                    width: `${meal4Pct}%`,
                    background: "linear-gradient(90deg,#FFD66B,#DEFF9A)",
                    boxShadow: "0 0 12px #DEFF9A66",
                  }}
                />
              </div>
              <div className="mt-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <span style={{ color: "#DEFF9A" }}>
                  {t("swap.meal4.percent").replace("{pct}", String(meal4Pct))}
                </span>
                <span className="truncate" title={MEAL4_TREASURY_WALLET}>
                  {t("swap.meal4.treasuryLabel")}: {shortTreasury(MEAL4_TREASURY_WALLET)}
                </span>
              </div>
            </div>

            {/* Direct protocol purchase widget (routes to project treasury) */}
            <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">

              <div className="flex items-center justify-between">
                <label
                  htmlFor="meal4-pay"
                  className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                >
                  {t("swap.from")}
                </label>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("swap.balance")}: --
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <input
                  id="meal4-pay"
                  value={meal4PayAmount}
                  onChange={(e) => setMeal4PayAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  inputMode="decimal"
                  placeholder="0.00"
                  className="w-0 flex-1 bg-transparent font-display text-3xl font-bold outline-none rounded-md focus-visible:ring-2 focus-visible:ring-saffron/60 placeholder:text-muted-foreground/30"
                  aria-label={t("swap.from")}
                />
                <div
                  role="radiogroup"
                  aria-label="Pay currency"
                  className="inline-flex items-center gap-1 rounded-full bg-white/5 p-1 text-[11px] font-bold"
                >
                  {(["USDC", "SOL"] as const).map((c) => {
                    const active = meal4PayCurrency === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setMeal4PayCurrency(c)}
                        className={cn(
                          "inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60 sm:min-h-0",
                          active ? "text-obsidian" : "text-foreground/70 hover:text-foreground",
                        )}
                        style={
                          active
                            ? { background: "linear-gradient(135deg,#FFD66B,#DEFF9A)" }
                            : undefined
                        }
                      >
                        <span
                          className={cn(
                            "h-3 w-3 rounded-full",
                            c === "USDC"
                              ? "bg-gradient-to-br from-blue-400 to-blue-600"
                              : "bg-gradient-to-br from-fuchsia-400 to-purple-600",
                          )}
                        />
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="my-2 flex justify-center">
                <div className="grid h-7 w-7 place-items-center rounded-full border border-white/10 bg-white/[0.03]">
                  <ArrowDown className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>
              <div
                className="rounded-xl border p-3"
                style={{ borderColor: "#DEFF9A40", background: "#DEFF9A08" }}
              >
                <div className="flex items-center justify-between">
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.18em]"
                    style={{ color: "#DEFF9A" }}
                  >
                    {t("swap.to")}
                  </div>
                  {meal4Quoting && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground animate-pulse">
                      {t("swap.quoting")}
                    </span>
                  )}
                </div>
                <div
                  className="mt-1 flex items-center justify-between min-h-[2.25rem]"
                  aria-live="polite"
                >
                  {meal4Quoting ? (
                    <div className="h-7 w-28 rounded-md bg-[#DEFF9A22] animate-pulse" aria-hidden />
                  ) : (
                    <div className="font-display text-2xl font-bold" style={{ color: "#DEFF9A" }}>
                      {meal4ReceiveAmount}
                    </div>
                  )}
                  <span
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold"
                    style={{ background: "#DEFF9A14", color: "#DEFF9A" }}
                  >
                    <img src={meal4Coin} alt="" width={16} height={16} loading="lazy" decoding="async" className="h-4 w-4" />
                    MEAL4
                  </span>
                </div>
              </div>
              <WidgetErrorBoundary surface="rpc_indicator" silent>
                <NetworkStatusNote status={treasury.status} />
              </WidgetErrorBoundary>
              <button
                type="button"
                onClick={() => {
                  track("swap_submitted", {
                    token: "MEAL4",
                    phase: meal4Flow.phase,
                    payAmount: meal4PayAmount,
                    payCurrency: meal4PayCurrency,
                    ...attributionProps("MEAL4"),
                  });
                  if (meal4Flow.phase === "idle") meal4Flow.arm();
                  else if (meal4Flow.phase === "confirm") void runPurchase("MEAL4");
                }}

                data-testid="meal4-purchase"
                disabled={meal4Quoting || busy(meal4Flow.phase) || !treasury.enabled}
                aria-live="polite"
                className={cn(
                  "liquid-btn mt-4 w-full rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian focus-visible:ring-saffron disabled:opacity-70",
                  meal4Flow.phase === "confirm" &&
                    "ring-2 ring-saffron/70 shadow-[0_0_0_4px_rgba(245,158,11,0.18)] scale-[1.01]",
                )}
                style={{
                  background:
                    meal4Flow.phase === "confirm"
                      ? "linear-gradient(135deg,#FFD66B,#DEFF9A)"
                      : "#DEFF9A",
                }}
              >
                {meal4Flow.phase === "idle" ? (
                  t("swap.meal4.cta")
                ) : meal4Flow.phase === "confirm" ? (
                  `${t("swap.meal4.confirm")} →`
                ) : (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {t(purchasePhaseKey(meal4Flow.phase))}
                  </span>
                )}
              </button>
              {notice?.token === "MEAL4" && (
                <TxStatusPanel notice={notice} onDismiss={() => setNotice(null)} />
              )}
              <TxTroubleshootTrigger signature={notice?.signature} />

              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                {t("swap.meal4.treasuryNote")}
              </p>
            </div>
          </article>


          {/* ===== RIGHT CARD — MEAL2 ===== */}
          <article data-token-card="MEAL2" className="relative flex flex-col rounded-3xl border border-white/10 bg-white/[0.015] p-6 sm:p-7">
            <header className="flex items-start justify-between gap-4">
              <div>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
                  style={{ borderColor: "#DAFFDE55", color: "#DAFFDE", background: "#DAFFDE0D" }}
                >
                  <Utensils className="h-3 w-3" />
                  {t("meal2.badge")}
                </span>
                <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight">
                  {t("meal2.title")}
                </h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t("meal2.subtitle")}
                </p>
              </div>
              <img src={meal2Coin} alt="" width={48} height={48} loading="lazy" decoding="async" className="h-12 w-12 shrink-0" />
            </header>

            <Tabs
              value={meal2Tab}
              onValueChange={(v) => setMeal2Tab(v as "web3" | "cash")}
              className="mt-5 flex flex-1 flex-col"
            >
              <TabsList className="grid w-full grid-cols-2 rounded-xl border border-white/10 bg-transparent p-1">
                <TabsTrigger
                  value="web3"
                  className="min-h-11 rounded-lg text-xs font-mono uppercase tracking-[0.14em] transition-all data-[state=active]:bg-white/[0.06] data-[state=active]:shadow-none data-[state=active]:text-[#DAFFDE] text-[#64748b] sm:min-h-0"
                >
                  {t("meal2.tab.web3")}
                </TabsTrigger>
                <TabsTrigger
                  value="cash"
                  className="min-h-11 rounded-lg text-xs font-mono uppercase tracking-[0.14em] transition-all data-[state=active]:bg-white/[0.06] data-[state=active]:shadow-none data-[state=active]:text-[#DAFFDE] text-[#64748b] sm:min-h-0"
                >
                  {t("meal2.tab.cash")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="web3" className="mt-5 flex-1">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t("meal2.web3desc")}
                </p>
                <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
                  {/* You Send */}
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="meal2-pay"
                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                      >
                        {t("swap.from")}
                      </label>
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {t("swap.balance")}: --
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <input
                        id="meal2-pay"
                        data-testid="meal2-pay-input"
                        type="text"
                        inputMode="decimal"
                        value={meal2PayAmount}
                        onChange={(e) => setMeal2PayAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                        placeholder="0.00"
                        className="w-0 flex-1 bg-transparent font-display text-2xl font-bold outline-none placeholder:text-muted-foreground/30 rounded-md focus-visible:ring-2 focus-visible:ring-[#DAFFDE]/60"
                        aria-label={t("swap.from")}
                      />
                      <div
                        role="radiogroup"
                        aria-label={t("swap.meal2.payWith")}
                        className="inline-flex items-center gap-1 rounded-full bg-white/5 p-1 text-[11px] font-bold"
                      >
                        {(["USDC", "SOL"] as const).map((c) => {
                          const active = meal2PayCurrency === c;
                          return (
                            <button
                              key={c}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              data-testid={`meal2-currency-${c.toLowerCase()}`}
                              onClick={() => setMeal2PayCurrency(c)}
                              className={cn(
                                "inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DAFFDE]/60 sm:min-h-0",
                                active ? "text-obsidian" : "text-foreground/70 hover:text-foreground",
                              )}
                              style={active ? { background: "#DAFFDE" } : undefined}
                            >
                              <span
                                className={cn(
                                  "h-3 w-3 rounded-full",
                                  c === "USDC"
                                    ? "bg-gradient-to-br from-blue-400 to-blue-600"
                                    : "bg-gradient-to-br from-fuchsia-400 to-purple-600",
                                )}
                              />
                              {c}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {meal2PayCurrency === "SOL" && (
                      <p
                        className="mt-2 font-mono text-[10px] leading-relaxed tracking-[0.12em] text-muted-foreground"
                        data-testid="meal2-sol-route"
                        aria-live="polite"
                      >
                        {t("swap.meal2.solRoute")} · 1 SOL ≈ $
                        {solPrice.price.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                        {solPrice.fallback ? ` · ${t("swap.meal2.priceFallback")}` : ""}
                        {meal2UsdValue > 0
                          ? ` · ≈ $${meal2UsdValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : ""}
                      </p>
                    )}
                    {meal2PayCurrency === "SOL" && (
                      <div
                        className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3"
                        data-testid="meal2-quote-panel"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                            {t("swap.quote.title")}
                          </span>
                          <span
                            className={cn(
                              "font-mono text-[10px] tracking-[0.12em]",
                              meal2QuoteRefreshing
                                ? "animate-pulse text-saffron"
                                : meal2QuoteSeconds === 0
                                  ? "text-coral"
                                  : "text-muted-foreground",
                            )}
                            data-testid="meal2-quote-countdown"
                            aria-live="polite"
                            aria-busy={meal2QuoteRefreshing}
                          >
                            {meal2QuoteRefreshing
                              ? t("swap.quote.refreshing")
                              : meal2QuoteSeconds === 0
                                ? t("swap.quote.expired")
                                : t("swap.quote.expiresIn").replace("{s}", String(meal2QuoteSeconds))}
                          </span>

                        </div>

                        <dl className="mt-2 grid gap-1 text-[11px]">
                          <div className="flex justify-between gap-3">
                            <dt className="text-muted-foreground">{t("swap.quote.usdValue")}</dt>
                            <dd className="font-mono" data-testid="meal2-quote-usd">
                              ${meal2Quote.usdValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-muted-foreground">{t("swap.quote.minReceive")}</dt>
                            <dd className="font-mono" data-testid="meal2-quote-min">
                              {meal2Quote.minReceive.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MEAL2
                            </dd>
                          </div>
                        </dl>

                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <div>
                            <label
                              htmlFor="meal2-slippage"
                              className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
                            >
                              {t("swap.quote.slippage")}
                            </label>
                            <select
                              id="meal2-slippage"
                              data-testid="meal2-slippage"
                              value={meal2Slippage}
                              onChange={(e) => {
                                setMeal2Slippage(clampSlippage(Number(e.target.value)));
                                track("quote_settings_changed", { field: "slippage", value: e.target.value });
                              }}
                              className="mt-1 min-h-11 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-xs text-foreground sm:min-h-0"
                            >
                              {SLIPPAGE_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}%</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              htmlFor="meal2-quote-ttl"
                              className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
                            >
                              {t("swap.quote.expiry")}
                            </label>
                            <select
                              id="meal2-quote-ttl"
                              data-testid="meal2-quote-ttl"
                              value={meal2QuoteTtl}
                              onChange={(e) => {
                                setMeal2QuoteTtl(clampTtl(Number(e.target.value)));
                                track("quote_settings_changed", { field: "ttl", value: e.target.value });
                              }}
                              className="mt-1 min-h-11 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-xs text-foreground sm:min-h-0"
                            >
                              {QUOTE_TTL_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}s</option>
                              ))}
                            </select>
                          </div>
                          <button
                            type="button"
                            data-testid="meal2-quote-refresh"
                            onClick={() => {
                              void solPrice.refresh?.();
                              setQuoteTick((v) => v + 1);
                              track("quote_refreshed", { token: "MEAL2" });
                            }}
                            className="mt-auto inline-flex min-h-11 items-center rounded-full border border-white/15 px-3 text-[11px] font-semibold text-foreground/80 hover:text-foreground sm:min-h-9"
                          >
                            {t("swap.quote.refresh")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Arrow */}
                  <div className="my-2 flex justify-center">
                    <div className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/[0.03]">
                      <ArrowDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  </div>

                  {/* Estimated Meals Secured */}
                  <div
                    className="rounded-xl border p-3"
                    style={{ borderColor: "#DAFFDE40", background: "#DAFFDE08" }}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className="font-mono text-[10px] uppercase tracking-[0.18em]"
                        style={{ color: "#DAFFDE" }}
                      >
                        {t("swap.to")}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {meal2Quoting ? t("swap.quoting") : t("swap.meal2.rateLabel")}
                      </span>
                    </div>
                    <div
                      className="mt-2 flex items-center justify-between gap-3 min-h-[2rem]"
                      aria-live="polite"
                    >
                      {meal2Quoting ? (
                        <div
                          className="h-6 w-24 rounded-md bg-[#DAFFDE22] animate-pulse"
                          aria-hidden
                        />
                      ) : (
                        <div
                          data-testid="meal2-receive"
                          className="w-0 flex-1 font-display text-2xl font-bold"
                          style={{ color: "#DAFFDE" }}
                        >
                          {meal2ReceiveAmount}
                        </div>
                      )}
                      <span
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold border"
                        style={{
                          background: "#DAFFDE14",
                          color: "#DAFFDE",
                          borderColor: "#DAFFDE30",
                        }}
                      >
                        <img src={meal2Coin} alt="" width={16} height={16} loading="lazy" decoding="async" className="h-4 w-4" />
                        MEAL2
                      </span>
                    </div>
                  </div>

                  <WidgetErrorBoundary surface="rpc_indicator" silent>
                    <NetworkStatusNote status={treasury.status} />
                  </WidgetErrorBoundary>
                  <button
                    type="button"
                    onClick={() => {
                      track("swap_submitted", {
                        token: "MEAL2",
                        phase: meal2Flow.phase,
                        payAmount: meal2PayAmount,
                        payCurrency: meal2PayCurrency,
                        ...attributionProps("MEAL2"),
                      });
                      if (meal2Flow.phase === "idle") meal2Flow.arm();
                      else if (meal2Flow.phase === "confirm") void runPurchase("MEAL2");
                    }}

                    data-testid="meal2-purchase"
                    disabled={meal2Quoting || busy(meal2Flow.phase) || !treasury.enabled}
                    aria-live="polite"
                    className={cn(
                      "liquid-btn mt-4 w-full rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian focus-visible:ring-[#DAFFDE] disabled:opacity-70",
                      meal2Flow.phase === "confirm" &&
                        "ring-2 ring-[#DAFFDE]/70 shadow-[0_0_0_4px_rgba(218,255,222,0.18)] scale-[1.01]",
                    )}
                    style={{
                      background:
                        meal2Flow.phase === "confirm"
                          ? "linear-gradient(135deg,#B8FFC9,#DAFFDE)"
                          : "#DAFFDE",
                    }}
                  >
                    {meal2Flow.phase === "idle" ? (
                      t("swap.meal2.cta")
                    ) : meal2Flow.phase === "confirm" ? (
                      `${t("swap.meal2.confirm")} →`
                    ) : (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        {t(purchasePhaseKey(meal2Flow.phase))}
                      </span>
                    )}
                  </button>
                  {notice?.token === "MEAL2" && (
                    <TxStatusPanel notice={notice} onDismiss={() => setNotice(null)} />
                  )}
                  <TxTroubleshootTrigger signature={notice?.signature} />


                  <div
                    className="mt-3 flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                    title={SFN_TREASURY_ADDRESS}
                  >
                    <span>{t("swap.meal4.treasuryLabel")}</span>
                    <span className="truncate text-foreground/70">
                      {shortTreasury(SFN_TREASURY_ADDRESS)}
                    </span>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="cash" className="mt-5 flex-1">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t("meal2.cashdesc")}
                </p>
                <ol className="mt-5 space-y-3">
                  {[
                    {
                      step: "1. Order & Pay",
                      desc: "Purchase your meal credits directly at the SFN Pilot Kitchen counter using traditional Cash (Thai Baht) or Credit Card.",
                      icon: Banknote,
                    },
                    {
                      step: "2. Scan Receipt QR",
                      desc: "Use your smartphone camera to scan the unique secure QR code printed onto your physical cash receipt.",
                      icon: ScanLine,
                    },
                    {
                      step: "3. Instant Synchronization",
                      desc: "Your MEAL2 balance loads instantly onto your web-app without requiring gas fees or crypto knowledge.",
                      icon: Receipt,
                    },
                  ].map((s, i) => (
                    <li
                      key={i}
                      className="group relative flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 transition-colors hover:bg-white/[0.04]"
                    >
                      <span
                        className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full font-mono text-[11px] font-bold text-obsidian"
                        style={{ background: "#DAFFDE" }}
                      >
                        {i + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <s.icon
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            style={{ color: "#DAFFDE" }}
                          />
                          <span className="text-sm font-semibold text-foreground">{s.step}</span>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          {s.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
                <button
                  type="button"
                  onClick={() => setShowRedemptionModal(true)}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl border px-5 py-3 text-sm font-bold uppercase tracking-wide transition hover:bg-white/[0.04]"
                  style={{ borderColor: "#DAFFDE80", color: "#DAFFDE" }}
                >
                  {t("meal2.viewGuide")}
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </TabsContent>
            </Tabs>
          </article>
        </div>

        {/* ===== TREASURY TRANSPARENCY ===== */}
        <TreasuryTransparency status={treasury.status} className="mt-8" />

        <p className="mt-8 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {t("barometer.syncNote")}
        </p>
      </div>

      {/* ===== PURCHASE RECEIPT MODAL ===== */}
      <WidgetErrorBoundary surface="receipt_modal" silent>
        <PurchaseConfirmModal receipt={receipt} onClose={() => setReceipt(null)} />
      </WidgetErrorBoundary>

      {/* ===== REDEMPTION MODAL ===== */}
      <RedemptionModal
        open={showRedemptionModal}
        onClose={() => setShowRedemptionModal(false)}
      />
    </section>
  );
}

function RedemptionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useModalA11y({
    open,
    onClose,
    analyticsId: "redemption",
    initialFocusRef: closeRef,
  });
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(15, 23, 42, 0.95)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="redemption-modal-title"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative w-full max-w-lg scale-in rounded-3xl border p-6 sm:p-8 focus:outline-none"
        style={{
          background: "rgba(15, 23, 42, 0.98)",
          borderColor: "#DAFFDE40",
          boxShadow: "0 0 60px rgba(218, 255, 222, 0.08), 0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close redemption guide"
          className="absolute right-4 top-4 rounded-full p-2 text-muted-foreground transition hover:text-foreground hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="text-center">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ borderColor: "#DAFFDE55", color: "#DAFFDE", background: "#DAFFDE0D" }}
          >
            <QrCode className="h-3 w-3" />
            {t("redemption.eyebrow")}
          </span>
          <h3
            id="redemption-modal-title"
            className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl"
          >
            {t("redemption.title")}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            {t("redemption.sub")}
          </p>
        </div>

        {/* Steps */}
        <div className="mt-6 space-y-4">
          {[
            {
              icon: CreditCard,
              title: "Step 1: Order & Pay traditionally",
              desc: "Walk up to the counter at the SFN Pilot Kitchen. Order your favorite authentic meal and pay using Thai Baht (THB) cash or your preferred credit card. Tell the cashier you want to load SFN credits.",
            },
            {
              icon: ScanLine,
              title: "Step 2: Scan your printed receipt",
              desc: "The cashier will hand you a receipt containing a secure, single-use SFN QR code. Open your smartphone camera or your integrated web-wallet and scan the code instantly.",
            },
            {
              icon: Wallet,
              title: "Step 3: Enjoy & Redeem",
              desc: "Your MEAL2 token balance updates on your screen in less than a second. No gas fees, no crypto purchase required. Use your balance to immediately unlock your meal or save it for your next visit!",
            },
          ].map((s, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4"
            >
              <span
                className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full"
                style={{ background: "#DAFFDE14", border: "1px solid #DAFFDE30" }}
              >
                <s.icon className="h-4 w-4" style={{ color: "#DAFFDE" }} />
              </span>
              <div>
                <h4 className="text-sm font-semibold text-foreground">{s.title}</h4>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Action button */}
        <button
          type="button"
          onClick={onClose}
          className="liquid-btn mt-6 w-full rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian"
          style={{ background: "#DAFFDE" }}
        >
          Got it, clear!
        </button>
      </div>
    </div>
  );
}

/* ---------------- TRUST ---------------- */
function Trust() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  return (
    <section className="py-24 sm:py-32">
      <div ref={r} className="reveal mx-auto max-w-5xl px-4 sm:px-6">
        <div className="grid gap-10 rounded-3xl glass luminous-border p-8 sm:p-12 md:grid-cols-[auto_1fr] md:items-start md:gap-12">
          <div className="relative">
            <div className="grid h-20 w-20 place-items-center rounded-2xl bg-gradient-to-br from-mint to-saffron text-obsidian glow-mint">
              <MapPin className="h-9 w-9" />
            </div>
          </div>
          <div>
            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-mint">
              // {t("trust.eyebrow")}
            </span>
            <h2 className="mt-3 font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl">
              {t("trust.title")}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              {t("trust.body")}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {[t("trust.b1"), t("trust.b2"), t("trust.b3")].map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1.5 rounded-full glass px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-foreground/80"
                >
                  <ShieldCheck className="h-3.5 w-3.5 text-mint" />
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ — Split-Screen Dashboard ---------------- */
type FaqKind = "pricing" | "scaling" | "global" | "difference";

function FAQ() {
  const { t } = useI18n();
  const items: { kind: FaqKind; q: string; a: string; tone: "saffron" | "mint" | "coral" }[] = [
    { kind: "pricing", q: t("faq.q1"), a: t("faq.a1"), tone: "saffron" },
    { kind: "scaling", q: t("faq.q2"), a: t("faq.a2"), tone: "mint" },
    { kind: "global", q: t("faq.q3"), a: t("faq.a3"), tone: "coral" },
    { kind: "difference", q: t("faq.q4"), a: t("faq.a4"), tone: "saffron" },
  ];
  const [openIdx, setOpenIdx] = useState<number>(2); // start on Meal4/Meal2 visual
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const activeIdx = hoverIdx ?? openIdx;
  const active = items[activeIdx];
  const r = useReveal<HTMLDivElement>();

  return (
    <section id="faq" className="relative overflow-hidden py-24 sm:py-32">
      {/* Ambient cinematic glow that shifts with active tone */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 transition-[background] duration-700"
        style={{
          background:
            active.tone === "mint"
              ? "radial-gradient(50% 50% at 80% 30%, oklch(0.86 0.16 165 / 0.18), transparent 60%), radial-gradient(40% 40% at 20% 80%, oklch(0.6 0.15 200 / 0.12), transparent 60%)"
              : active.tone === "coral"
                ? "radial-gradient(50% 50% at 80% 30%, oklch(0.74 0.19 30 / 0.18), transparent 60%), radial-gradient(40% 40% at 20% 80%, oklch(0.82 0.18 75 / 0.10), transparent 60%)"
                : "radial-gradient(50% 50% at 80% 30%, oklch(0.82 0.18 75 / 0.18), transparent 60%), radial-gradient(40% 40% at 20% 80%, oklch(0.74 0.19 30 / 0.12), transparent 60%)",
        }}
      />

      <div ref={r} className="reveal mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            // {t("faq.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("faq.title")}
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">{t("faq.sub")}</p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-[1.05fr_1fr] md:gap-8">
          {/* LEFT — Glass accordion list */}
          <div className="flex flex-col gap-3">
            {items.map((it, i) => {
              const isOpen = openIdx === i;
              const isHot = activeIdx === i;
              return (
                <FaqRow
                  key={i}
                  index={i}
                  item={it}
                  isOpen={isOpen}
                  isHot={isHot}
                  onToggle={() => setOpenIdx(isOpen ? -1 : i)}
                  onHover={(h) => setHoverIdx(h ? i : null)}
                />
              );
            })}
          </div>

          {/* RIGHT — Reactive Canvas */}
          <div className="relative min-h-[420px] rounded-3xl glass-strong luminous-border overflow-hidden">
            <FaqCanvas kind={active.kind} tone={active.tone} />
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqRow({
  index,
  item,
  isOpen,
  isHot,
  onToggle,
  onHover,
}: {
  index: number;
  item: { kind: FaqKind; q: string; a: string; tone: "saffron" | "mint" | "coral" };
  isOpen: boolean;
  isHot: boolean;
  onToggle: () => void;
  onHover: (h: boolean) => void;
}) {
  const tone = item.tone;
  const glow =
    tone === "mint"
      ? "0 0 0 1px oklch(0.86 0.16 165 / 0.35), 0 20px 60px -20px oklch(0.86 0.16 165 / 0.45)"
      : tone === "coral"
        ? "0 0 0 1px oklch(0.74 0.19 30 / 0.35), 0 20px 60px -20px oklch(0.74 0.19 30 / 0.45)"
        : "0 0 0 1px oklch(0.82 0.18 75 / 0.35), 0 20px 60px -20px oklch(0.82 0.18 75 / 0.45)";

  // Magnetic cursor tracking for ambient glow
  const ref = useRef<HTMLDivElement | null>(null);
  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    ref.current!.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current!.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const toneColor =
    tone === "mint"
      ? "oklch(0.86 0.16 165)"
      : tone === "coral"
        ? "oklch(0.74 0.19 30)"
        : "oklch(0.82 0.18 75)";

  return (
    <div
      ref={ref}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onMouseMove={onMove}
      className="group relative overflow-hidden rounded-2xl glass transition-all duration-500"
      style={{
        boxShadow: isHot ? glow : undefined,
        transform: isHot ? "translateY(-2px)" : "translateY(0)",
      }}
    >
      {/* Magnetic ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: `radial-gradient(220px circle at var(--mx, 50%) var(--my, 50%), ${toneColor.replace(")", " / 0.18)")}, transparent 60%)`,
        }}
      />
      <button
        type="button"
        onClick={() => {
          if (!isOpen) track("faq_open", { id: item.kind, index });
          onToggle();
        }}
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${index}`}
        id={`faq-trigger-${index}`}
        className="relative z-10 flex w-full items-center gap-3 px-4 py-4 text-left cursor-pointer sm:gap-4 sm:px-6 sm:py-5 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/70 focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian min-h-[64px]"
      >
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg glass transition-transform duration-500 sm:h-11 sm:w-11 sm:rounded-xl"
          style={{
            boxShadow: `inset 0 0 0 1px ${toneColor.replace(")", " / 0.25)")}`,
            transform: isHot ? "rotate(8deg) scale(1.06)" : "rotate(0) scale(1)",
          }}
        >
          <FaqIcon kind={item.kind} color={toneColor} animated={isHot} />
        </span>
        <span className="flex-1 font-display text-[15px] font-medium leading-snug tracking-tight text-foreground sm:text-lg break-words">
          {item.q}
        </span>
        <span
          aria-hidden
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full glass font-mono text-[10px] text-muted-foreground transition-transform duration-500"
          style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0)" }}
        >
          +
        </span>
      </button>

      {/* Fluid-height expand — GPU-accelerated grid-row transition */}
      <div
        id={`faq-panel-${index}`}
        role="region"
        aria-labelledby={`faq-trigger-${index}`}
        hidden={!isOpen}
        className="relative z-10 grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] will-change-[grid-template-rows]"
        style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pl-4 pr-4 sm:px-6 sm:pb-5 sm:pl-[5.5rem] sm:pr-6">
            <p
              key={`${index}-${isOpen ? "o" : "c"}`}
              className="text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]"
              style={{ animation: isOpen ? "fade-in 0.6s ease-out 0.08s both" : undefined }}
            >
              <StaggerText text={item.a} active={isOpen} />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Staggered word reveal — sophisticated without per-letter weight */
function StaggerText({ text, active }: { text: string; active: boolean }) {
  const words = text.split(" ");
  return (
    <>
      {words.map((w, i) => (
        <span
          key={i}
          className="inline-block"
          style={{
            opacity: active ? 1 : 0,
            transform: active ? "translateY(0)" : "translateY(6px)",
            transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
            transitionDelay: active ? `${120 + i * 18}ms` : "0ms",
          }}
        >
          {w}&nbsp;
        </span>
      ))}
    </>
  );
}

/** Custom animated SVG icons per question kind */
function FaqIcon({ kind, color, animated }: { kind: FaqKind; color: string; animated: boolean }) {
  const dur = animated ? "2.4s" : "0s";
  if (kind === "pricing") {
    return (
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="6" width="18" height="13" rx="3" />
        <path d="M3 10h14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H3" />
        <circle cx="16" cy="13" r="1.2" fill={color}>
          {animated && (
            <animate attributeName="r" values="1;1.6;1" dur={dur} repeatCount="indefinite" />
          )}
        </circle>
      </svg>
    );
  }
  if (kind === "scaling") {
    return (
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" fill={color} fillOpacity="0.15">
          {animated && (
            <animate
              attributeName="fill-opacity"
              values="0.1;0.55;0.1"
              dur={dur}
              repeatCount="indefinite"
            />
          )}
        </path>
      </svg>
    );
  }
  if (kind === "global") {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke={color} strokeWidth="1.6">
        <circle cx="9" cy="12" r="5">
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 9 12"
              to="360 9 12"
              dur="8s"
              repeatCount="indefinite"
            />
          )}
        </circle>
        <circle cx="15" cy="12" r="5" opacity="0.65">
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="360 15 12"
              to="0 15 12"
              dur="8s"
              repeatCount="indefinite"
            />
          )}
        </circle>
      </svg>
    );
  }
  // gift / network
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="9" width="18" height="12" rx="2" />
      <path d="M3 13h18M12 9v12M8 9c-2 0-3-3 0-3s4 3 4 3-2-3-4-3M16 9c2 0 3-3 0-3s-4 3-4 3 2-3 4-3" />
      {animated && (
        <circle cx="12" cy="6" r="1" fill={color}>
          <animate attributeName="cy" values="6;3;6" dur={dur} repeatCount="indefinite" />
        </circle>
      )}
    </svg>
  );
}

/** Reactive right-side canvas — morphs per active question */
function FaqCanvas({ kind, tone }: { kind: FaqKind; tone: "saffron" | "mint" | "coral" }) {
  const { t } = useI18n();
  const toneColor =
    tone === "mint"
      ? "oklch(0.86 0.16 165)"
      : tone === "coral"
        ? "oklch(0.74 0.19 30)"
        : "oklch(0.82 0.18 75)";
  const label =
    kind === "pricing"
      ? "PRICE · STABILITY"
      : kind === "scaling"
        ? "SCALE · NETWORK"
        : kind === "global"
          ? "GLOBAL · AID"
          : "CONTRACT · UTILITY";

  return (
    <div className="relative h-full w-full">
      {/* Grid background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, oklch(1 0 0 / 0.08) 1px, transparent 1px), linear-gradient(to bottom, oklch(1 0 0 / 0.08) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
        }}
      />
      {/* Bloom */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 transition-[background] duration-700"
        style={{
          background: `radial-gradient(50% 50% at 50% 50%, ${toneColor.replace(")", " / 0.25)")}, transparent 70%)`,
        }}
      />

      {/* Label */}
      <div className="absolute left-5 top-5 z-10 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: toneColor, boxShadow: `0 0 12px ${toneColor}` }}
        />
        {label}
      </div>
      <div className="absolute right-5 top-5 z-10 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        // canvas.v2
      </div>

      {/* Morphing visual */}
      <div className="absolute inset-0 grid place-items-center">
        <div
          key={kind}
          className="relative h-[280px] w-[280px]"
          style={{ animation: "scale-in 0.7s cubic-bezier(0.2,0.8,0.2,1)" }}
        >
          {kind === "pricing" && <CanvasWallet color={toneColor} />}
          {kind === "scaling" && <CanvasPower color={toneColor} />}
          {kind === "global" && <CanvasTokens />}
          {kind === "difference" && <CanvasNetwork color={toneColor} />}
        </div>
      </div>

      {/* Caption bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-background/30 px-5 py-3 backdrop-blur">
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>{t("canvas.simActive")}</span>
          <span style={{ color: toneColor }}>{kind.toUpperCase()}</span>
        </div>
      </div>
    </div>
  );
}

function CanvasWallet({ color }: { color: string }) {
  const { t } = useI18n();
  return (
    <svg viewBox="0 0 280 280" className="h-full w-full">
      <defs>
        <radialGradient id="wg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="140" cy="140" r="110" fill="url(#wg)" />
      <g stroke={color} strokeWidth="1.2" fill="none">
        <rect x="70" y="100" width="140" height="90" rx="14" />
        <path d="M70 130 H210" />
        <circle cx="180" cy="155" r="6" fill={color} fillOpacity="0.6" />
      </g>
      {/* Envelope morphing into wallet */}
      <g stroke={color} strokeWidth="1" opacity="0.5">
        <path d="M70 100 L140 60 L210 100" fill="none">
          <animate
            attributeName="d"
            values="M70 100 L140 60 L210 100; M70 100 L140 130 L210 100; M70 100 L140 60 L210 100"
            dur="4s"
            repeatCount="indefinite"
          />
        </path>
      </g>
      <text
        x="140"
        y="240"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="9"
        fill="oklch(0.7 0.01 250)"
        letterSpacing="2"
      >
        {t("canvas.bulkSourcing")}
      </text>
    </svg>
  );
}

function CanvasPower({ color }: { color: string }) {
  const { t } = useI18n();
  return (
    <svg viewBox="0 0 280 280" className="h-full w-full">
      <defs>
        <linearGradient id="pg" x1="0" x2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.8" />
          <stop offset="100%" stopColor={color} stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <circle
        cx="140"
        cy="140"
        r="100"
        fill="none"
        stroke={color}
        strokeOpacity="0.2"
        strokeDasharray="3 5"
      />
      <circle cx="140" cy="140" r="70" fill="none" stroke={color} strokeOpacity="0.35" />
      <path d="M150 70 L110 150 H140 L130 220 L180 130 H150 Z" fill="url(#pg)" stroke={color}>
        <animate
          attributeName="fill-opacity"
          values="0.4;1;0.4"
          dur="2.4s"
          repeatCount="indefinite"
        />
      </path>
      {/* Voucher chips */}
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={i}
          x={40 + i * 50}
          y="230"
          width="36"
          height="14"
          rx="3"
          fill="none"
          stroke={color}
          strokeOpacity="0.5"
        />
      ))}
      <text
        x="140"
        y="262"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="9"
        fill="oklch(0.7 0.01 250)"
        letterSpacing="2"
      >
        {t("canvas.networkScaling")}
      </text>
    </svg>
  );
}

function CanvasTokens() {
  const { t } = useI18n();
  return (
    <svg viewBox="0 0 280 280" className="h-full w-full">
      <defs>
        <radialGradient id="t1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="oklch(0.82 0.18 75)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="oklch(0.82 0.18 75)" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="t2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="oklch(0.86 0.16 165)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="oklch(0.86 0.16 165)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="tflow" x1="0" x2="1">
          <stop offset="0%" stopColor="oklch(0.82 0.18 75)" />
          <stop offset="100%" stopColor="oklch(0.86 0.16 165)" />
        </linearGradient>
      </defs>
      <circle
        cx="140"
        cy="140"
        r="120"
        fill="none"
        stroke="oklch(1 0 0 / 0.08)"
        strokeDasharray="2 6"
      />
      {/* Meal4 orb */}
      <g>
        <circle cx="80" cy="140" r="46" fill="url(#t1)" />
        <circle
          cx="80"
          cy="140"
          r="26"
          fill="none"
          stroke="oklch(0.82 0.18 75)"
          strokeWidth="1.2"
        />
        <text
          x="80"
          y="145"
          textAnchor="middle"
          fontFamily="Fraunces"
          fontSize="14"
          fill="oklch(0.82 0.18 75)"
          fontWeight="700"
        >
          M4
        </text>
      </g>
      {/* Meal2 orb */}
      <g>
        <circle cx="200" cy="140" r="46" fill="url(#t2)" />
        <circle
          cx="200"
          cy="140"
          r="26"
          fill="none"
          stroke="oklch(0.86 0.16 165)"
          strokeWidth="1.2"
        />
        <text
          x="200"
          y="145"
          textAnchor="middle"
          fontFamily="Fraunces"
          fontSize="14"
          fill="oklch(0.86 0.16 165)"
          fontWeight="700"
        >
          M2
        </text>
      </g>
      {/* Flow lines */}
      <path
        d="M106 130 C 140 100, 160 100, 174 130"
        fill="none"
        stroke="url(#tflow)"
        strokeWidth="1.5"
        strokeDasharray="6 8"
      >
        <animate
          attributeName="stroke-dashoffset"
          values="0;-28"
          dur="1.6s"
          repeatCount="indefinite"
        />
      </path>
      <path
        d="M174 150 C 140 180, 140 180, 106 150"
        fill="none"
        stroke="url(#tflow)"
        strokeWidth="1.5"
        strokeDasharray="6 8"
      >
        <animate
          attributeName="stroke-dashoffset"
          values="0;28"
          dur="1.6s"
          repeatCount="indefinite"
        />
      </path>
      <text
        x="140"
        y="240"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="9"
        fill="oklch(0.7 0.01 250)"
        letterSpacing="2"
      >
        {t("canvas.mealSponsor")}
      </text>
    </svg>
  );
}

function CanvasNetwork({ color }: { color: string }) {
  const { t } = useI18n();
  const nodes = [
    { x: 140, y: 80 },
    { x: 60, y: 150 },
    { x: 220, y: 150 },
    { x: 100, y: 220 },
    { x: 180, y: 220 },
  ];
  return (
    <svg viewBox="0 0 280 280" className="h-full w-full">
      {nodes.slice(1).map((n, i) => (
        <line
          key={i}
          x1={140}
          y1={80}
          x2={n.x}
          y2={n.y}
          stroke={color}
          strokeOpacity="0.35"
          strokeDasharray="3 5"
        >
          <animate
            attributeName="stroke-dashoffset"
            values="0;-16"
            dur={`${1.4 + i * 0.2}s`}
            repeatCount="indefinite"
          />
        </line>
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r="14" fill="none" stroke={color} strokeOpacity="0.4" />
          <circle cx={n.x} cy={n.y} r="6" fill={color} fillOpacity={i === 0 ? "0.9" : "0.55"}>
            {i === 0 && (
              <animate attributeName="r" values="6;9;6" dur="2s" repeatCount="indefinite" />
            )}
          </circle>
        </g>
      ))}
      <text
        x="140"
        y="262"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="9"
        fill="oklch(0.7 0.01 250)"
        letterSpacing="2"
      >
        {t("canvas.realContract")}
      </text>
    </svg>
  );
}

/* ---------------- TOKENOMICS — Living Ecosystem ---------------- */
type TokenoMetric = {
  key: string;
  label: string;
  value: string;
  desc: string;
  tone: "saffron" | "mint" | "coral" | "violet";
  icon: React.ComponentType<{ className?: string }>;
  details: {
    explanation: string;
    stats: { label: string; value: string }[];
  };
};

const TONE_BG: Record<TokenoMetric["tone"], string> = {
  saffron:
    "radial-gradient(60% 50% at 20% 30%, oklch(0.82 0.18 75 / 0.28), transparent 65%), radial-gradient(50% 40% at 85% 70%, oklch(0.74 0.19 30 / 0.18), transparent 60%)",
  mint: "radial-gradient(60% 50% at 80% 25%, oklch(0.86 0.16 165 / 0.28), transparent 65%), radial-gradient(50% 40% at 20% 75%, oklch(0.6 0.15 200 / 0.18), transparent 60%)",
  coral:
    "radial-gradient(60% 50% at 30% 70%, oklch(0.74 0.19 30 / 0.30), transparent 65%), radial-gradient(50% 40% at 80% 20%, oklch(0.82 0.18 75 / 0.16), transparent 60%)",
  violet:
    "radial-gradient(60% 50% at 70% 30%, oklch(0.6 0.18 280 / 0.28), transparent 65%), radial-gradient(50% 40% at 25% 75%, oklch(0.7 0.14 230 / 0.16), transparent 60%)",
};

import { TokenoModal, TONE_COLOR } from "@/components/tokenomics/TokenoModal";

/**
 * Static, language-independent metric definitions. Human-readable copy
 * (label, desc, explanation, stat labels & values) is resolved at render
 * time via t("tokeno.<key>.*") so the toggle, chart legend, tooltips,
 * card body, and modal all update when the user switches EN ↔ DE.
 */
type MetricBase = {
  key: string;
  value: string;
  tone: TokenoMetric["tone"];
  icon: React.ComponentType<{ className?: string }>;
};

const MEAL4_METRIC_BASE: MetricBase[] = [
  { key: "m4-stability", value: "40%", tone: "mint", icon: ShieldCheck },
  { key: "m4-collateral", value: "30%", tone: "saffron", icon: Building2 },
  { key: "m4-procurement", value: "20%", tone: "violet", icon: Coins },
  { key: "m4-safety", value: "10%", tone: "coral", icon: Activity },
];

const MEAL2_METRIC_BASE: MetricBase[] = [
  { key: "m2-operations", value: "45%", tone: "mint", icon: Utensils },
  { key: "m2-loyalty", value: "25%", tone: "saffron", icon: Users },
  { key: "m2-social", value: "20%", tone: "violet", icon: ShieldCheck },
  { key: "m2-growth", value: "10%", tone: "coral", icon: Activity },
];

type TokenId = "MEAL4" | "MEAL2";

const TOKEN_BASE: Record<TokenId, { id: TokenId; accent: string; base: MetricBase[] }> = {
  MEAL4: { id: "MEAL4", accent: "#DEFF9A", base: MEAL4_METRIC_BASE },
  MEAL2: { id: "MEAL2", accent: "#DAFFDE", base: MEAL2_METRIC_BASE },
};

function buildMetric(base: MetricBase, t: (k: string) => string): TokenoMetric {
  return {
    key: base.key,
    value: base.value,
    tone: base.tone,
    icon: base.icon,
    label: t(`tokeno.${base.key}.label`),
    desc: t(`tokeno.${base.key}.desc`),
    details: {
      explanation: t(`tokeno.${base.key}.expl`),
      stats: [
        { label: t(`tokeno.${base.key}.s1l`), value: t(`tokeno.${base.key}.s1v`) },
        { label: t(`tokeno.${base.key}.s2l`), value: t(`tokeno.${base.key}.s2v`) },
        { label: t(`tokeno.${base.key}.s3l`), value: t(`tokeno.${base.key}.s3v`) },
        { label: t(`tokeno.${base.key}.s4l`), value: t(`tokeno.${base.key}.s4v`) },
      ],
    },
  };
}

/** prefers-reduced-motion hook (SSR-safe). */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

/** Parse `?token=meal4|meal2` from either `location.search` or the
 *  hash-tail variant `#tokenomics?token=meal4` used in shareable links. */
function readTokenFromUrl(): TokenId | null {
  if (typeof window === "undefined") return null;
  try {
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    const hashQuery = hash.includes("?") ? hash.slice(hash.indexOf("?")) : "";
    const params = new URLSearchParams(search || hashQuery);
    const raw = (params.get("token") || "").toLowerCase();
    if (raw === "meal4") return "MEAL4";
    if (raw === "meal2") return "MEAL2";
  } catch {
    /* ignore */
  }
  return null;
}

function writeTokenToUrl(token: TokenId) {
  if (typeof window === "undefined") return;
  try {
    const next = `#tokenomics?token=${token.toLowerCase()}`;
    window.history.replaceState(null, "", next);
  } catch {
    /* ignore */
  }
}

function Tokenomics() {
  const { t } = useI18n();
  const reduceMotion = usePrefersReducedMotion();
  const [token, setToken] = useState<TokenId>("MEAL4");
  const [isLoading, setIsLoading] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  const profile = TOKEN_BASE[token];
  const metrics: TokenoMetric[] = profile.base.map((b) => buildMetric(b, t));

  const [activeKey, setActiveKey] = useState<string>(metrics[0].key);
  const active = metrics.find((m) => m.key === activeKey) ?? metrics[0];
  const [selectedMetric, setSelectedMetric] = useState<TokenoMetric | null>(null);
  const r = useReveal<HTMLDivElement>();
  void TONE_COLOR;

  // Deep-link: read ?token= on mount, scroll into view, and react to
  // popstate / hashchange (e.g. user shares a link or hits back).
  useEffect(() => {
    const apply = (scroll: boolean) => {
      const fromUrl = readTokenFromUrl();
      if (fromUrl) {
        setToken(fromUrl);
        setActiveKey(TOKEN_BASE[fromUrl].base[0].key);
      }
      if (
        scroll &&
        typeof window !== "undefined" &&
        window.location.hash.startsWith("#tokenomics")
      ) {
        // wait one frame so layout is ready
        requestAnimationFrame(() => {
          sectionRef.current?.scrollIntoView({
            behavior: reduceMotion ? "auto" : "smooth",
            block: "start",
          });
        });
      }
    };
    apply(true);
    const onChange = () => apply(false);
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    return () => {
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle handler with skeleton transition + URL sync.
  const handleSwitch = (next: TokenId) => {
    if (next === token) return;
    const finalize = () => {
      setToken(next);
      setActiveKey(TOKEN_BASE[next].base[0].key);
      setHasInteracted(false);
      setIsLoading(false);
      writeTokenToUrl(next);
    };
    if (reduceMotion) {
      finalize();
    } else {
      setIsLoading(true);
      window.setTimeout(finalize, 280);
    }
  };

  const onSegmentActivated = (key: string) => {
    setActiveKey(key);
    setHasInteracted(true);
    const m = metrics.find((x) => x.key === key);
    if (m) {
      const msg = t("tokeno.sr.activated")
        .replace("{label}", m.label)
        .replace("{value}", String(parseInt(m.value, 10) || 0));
      setAnnouncement(msg);
    }
  };

  return (
    <section
      id="tokenomics"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="relative overflow-hidden py-20 sm:py-28 md:py-32"
      aria-busy={isLoading}
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10 transition-[background] duration-700"
        style={{ background: TONE_BG[active.tone] }}
      />

      <div ref={r} className="reveal mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-mint">
            {t("tokeno.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
            {t("tokeno.title")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base md:text-lg">
            {t("tokeno.sub")}
          </p>
        </div>

        {/* Token toggle — centered, full-width on mobile */}
        <div
          role="tablist"
          aria-label={t("tokeno.toggleAria")}
          className="mx-auto mt-8 flex w-full max-w-sm items-center gap-1 rounded-full glass p-1 sm:max-w-md sm:mt-10"
        >
          {(Object.keys(TOKEN_BASE) as TokenId[]).map((id) => {
            const isActive = token === id;
            const accent = TOKEN_BASE[id].accent;
            return (
              <button
                key={id}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-controls="tokenomics-distribution-panel"
                onClick={() => handleSwitch(id)}
                className="flex-1 min-h-11 min-w-0 rounded-full px-3 py-2 text-[11px] sm:text-xs font-mono uppercase tracking-[0.18em] sm:tracking-[0.22em] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                style={{
                  background: isActive ? `${accent}20` : "transparent",
                  color: isActive ? accent : "rgba(255,255,255,0.65)",
                  boxShadow: isActive
                    ? `inset 0 0 0 1px ${accent}66, 0 0 18px ${accent}33`
                    : "none",
                }}
              >
                <span
                  aria-hidden
                  className="mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full align-middle"
                  style={{ background: accent, boxShadow: isActive ? `0 0 8px ${accent}` : "none" }}
                />
                {id}
              </button>
            );
          })}
        </div>
        <div className="mx-auto mt-3 max-w-md px-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {t(`tokeno.${token.toLowerCase()}.tagline`)}
        </div>

        {/* Screen-reader live region for segment + token changes */}
        <div role="status" aria-live="polite" className="sr-only">
          {isLoading ? t("tokeno.loading") : announcement}
        </div>

        <div
          id="tokenomics-distribution-panel"
          role="tabpanel"
          className="mt-10 grid gap-6 sm:gap-8 md:mt-12 md:grid-cols-[1.1fr_1fr] md:items-center"
        >
          {isLoading ? (
            <TokenomicsSkeleton label={t("tokeno.loading")} />
          ) : (
            <EcosystemFlow
              activeKey={active.key}
              segments={metrics.map((m) => ({
                key: m.key,
                label: m.label,
                value: parseInt(m.value, 10) || 0,
                color: TONE_COLOR[m.tone],
              }))}
              headerTitle={t(`tokeno.${token.toLowerCase()}.distTitle`)}
              supplyLabel={t(`tokeno.${token.toLowerCase()}.supply`)}
              accent={profile.accent}
              centerAllocLabel={t("tokeno.centerAlloc")}
              chartSummaryLabel={t("tokeno.sr.chartSummary")}
              chartHint={t("tokeno.sr.chartHint")}
              legendAriaLabel={t("tokeno.sr.legendAria")}
              segmentSrTemplate={t("tokeno.sr.segment")}
              segmentHint={t("tokeno.sr.segmentHint")}
              reduceMotion={reduceMotion}
              onSelect={onSegmentActivated}
              onActivate={(key: string) => {
                const m = metrics.find((x) => x.key === key);
                if (m) setSelectedMetric(m);
              }}
            />
          )}

          {isLoading ? (
            <div
              className="h-[360px] rounded-3xl glass animate-pulse"
              style={{ animationDuration: reduceMotion ? "0s" : undefined }}
              aria-hidden
            />
          ) : (
            <TokenomicsPreview
              metric={hasInteracted ? active : null}
              accent={profile.accent}
              reduceMotion={reduceMotion}
              labels={{
                eyebrowIdle: t("tokeno.preview.eyebrow"),
                eyebrowActive: t("tokeno.preview.activeEyebrow"),
                defaultMessage: t("tokeno.preview.default"),
                allocation: t("tokeno.preview.allocation"),
                cta: t("tokeno.preview.cta"),
                openAria: t("tokeno.preview.openAria"),
              }}
              onOpen={() => {
                if (active) setSelectedMetric(active);
              }}
            />
          )}
        </div>

        {/* Allocation cards — full width below the chart + preview */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-32 rounded-2xl glass animate-pulse"
                  style={{ animationDuration: reduceMotion ? "0s" : undefined }}
                />
              ))
            : metrics.map((m, i) => (
                <TokenoCard
                  key={m.key}
                  metric={m}
                  isActive={active.key === m.key}
                  delay={i}
                  onHover={() => onSegmentActivated(m.key)}
                  onClick={() => setSelectedMetric(m)}
                />
              ))}
        </div>

        {/* Interaction explainer */}
        <div className="mx-auto mt-10 max-w-4xl rounded-2xl glass p-5 sm:mt-12 sm:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t("tokeno.interactEyebrow")}
          </div>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
            <span className="font-semibold text-foreground">{t("tokeno.interact.meal4")}</span>{" "}
            {t("tokeno.interactBody")}{" "}
            <span className="font-semibold text-foreground">{t("tokeno.interact.meal2")}</span>{" "}
            {t("tokeno.interactBody2")}
          </p>
        </div>
      </div>

      {selectedMetric && (
        <TokenoModal
          metric={selectedMetric}
          onClose={() => setSelectedMetric(null)}
          labels={{
            toneSuffix: t("tokeno.modal.toneSuffix"),
            detailView: t("tokeno.modal.detailView"),
            verified: t("tokeno.modal.verified"),
            closeAria: t("tokeno.modal.closeAria"),
            valueAria: t("tokeno.modal.valueAria"),
          }}
        />
      )}
    </section>
  );
}

function TokenomicsSkeleton({ label }: { label: string }) {
  return (
    <div
      className="relative w-full max-w-[440px] mx-auto rounded-3xl glass-strong overflow-hidden p-5 sm:p-7"
      aria-label={label}
    >
      <div className="flex items-center justify-between">
        <div className="h-3 w-32 rounded-full bg-white/10 animate-pulse" />
        <div className="h-3 w-24 rounded-full bg-white/10 animate-pulse" />
      </div>
      <div className="relative mx-auto mt-4 aspect-square w-full max-w-[320px]">
        <div className="absolute inset-0 rounded-full border-[36px] border-white/[0.06] animate-pulse" />
        <div className="absolute inset-10 rounded-full bg-white/[0.03]" />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-3 w-full rounded-full bg-white/10 animate-pulse" />
        ))}
      </div>
    </div>
  );
}

type TokenomicsPreviewLabels = {
  eyebrowIdle: string;
  eyebrowActive: string;
  defaultMessage: string;
  allocation: string;
  cta: string;
  openAria: string;
};

function TokenomicsPreview({
  metric,
  accent,
  reduceMotion,
  labels,
  onOpen,
}: {
  metric: TokenoMetric | null;
  accent: string;
  reduceMotion: boolean;
  labels: TokenomicsPreviewLabels;
  onOpen: () => void;
}) {
  const isActive = metric !== null;
  const color = metric ? TONE_COLOR[metric.tone] : accent;
  const Icon = metric?.icon;
  const animKey = metric?.key ?? "idle";
  const anim = reduceMotion
    ? undefined
    : "fade-in 0.4s ease-out, scale-in 0.35s cubic-bezier(0.2,0.8,0.2,1)";

  return (
    <div
      className="relative h-full min-h-[360px] w-full overflow-hidden rounded-3xl glass-strong p-6 sm:p-8 flex flex-col transition-all duration-500"
      style={{
        boxShadow: isActive
          ? `0 30px 80px -40px ${color}, 0 0 0 1px oklch(1 0 0 / 0.08) inset`
          : `0 20px 60px -40px ${accent}, 0 0 0 1px oklch(1 0 0 / 0.06) inset`,
      }}
      aria-live="polite"
    >
      {/* ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50 transition-opacity duration-700"
        style={{
          background: `radial-gradient(70% 55% at 75% 30%, ${color}33, transparent 70%)`,
          opacity: isActive ? 0.7 : 0.35,
        }}
      />

      {/* eyebrow */}
      <div className="relative flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{
              background: color,
              boxShadow: `0 0 10px ${color}`,
              animation: !reduceMotion ? "pulse 2.4s ease-in-out infinite" : undefined,
            }}
          />
          {isActive ? labels.eyebrowActive : labels.eyebrowIdle}
        </span>
        {isActive && (
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px]" style={{ color }}>
            <Sparkles className="h-3 w-3" aria-hidden />
            LIVE
          </span>
        )}
      </div>

      {/* body */}
      <div key={animKey} className="relative mt-6 flex-1 flex flex-col" style={{ animation: anim }}>
        {!isActive || !metric || !Icon ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <div
              aria-hidden
              className="mb-5 grid h-16 w-16 place-items-center rounded-2xl glass-strong"
              style={{
                boxShadow: `0 0 30px ${accent}55`,
                animation: !reduceMotion ? "pulse 3s ease-in-out infinite" : undefined,
              }}
            >
              <Sparkle className="h-7 w-7" style={{ color: accent }} aria-hidden />
            </div>
            <p
              className="max-w-sm text-balance text-sm leading-relaxed text-foreground/85 sm:text-base"
              style={{
                textShadow: !reduceMotion ? `0 0 24px ${accent}33` : undefined,
              }}
            >
              {labels.defaultMessage}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <div
                className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl glass-strong"
                style={{ boxShadow: `0 0 20px ${color}` }}
                aria-hidden
              >
                <span style={{ color }}>
                  <Icon className="h-6 w-6" />
                </span>
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {labels.allocation}
                </div>
                <h3 className="mt-0.5 font-display text-xl font-medium leading-tight tracking-tight sm:text-2xl">
                  {metric.label}
                </h3>
              </div>
            </div>

            <div
              className="mt-5 font-display font-medium leading-none tracking-tight"
              style={{ color, fontSize: "clamp(3.5rem, 9vw, 5rem)" }}
            >
              {metric.value}
            </div>

            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
              {metric.desc}
            </p>

            <button
              type="button"
              onClick={onOpen}
              aria-label={labels.openAria.replace("{label}", metric.label)}
              className="group mt-auto inline-flex w-full items-center justify-between gap-3 rounded-2xl glass px-4 py-3.5 text-left transition-all duration-300 hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 cursor-pointer"
              style={{
                boxShadow: `inset 0 0 0 1px ${color}44`,
              }}
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color }}>
                {labels.cta}
              </span>
              <span
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full transition-transform duration-300 group-hover:translate-x-1"
                style={{
                  background: `${color}22`,
                  boxShadow: `0 0 14px ${color}66`,
                }}
                aria-hidden
              >
                <ArrowRight
                  className="h-4 w-4"
                  style={{
                    color,
                    animation: !reduceMotion ? "pulse 2s ease-in-out infinite" : undefined,
                  }}
                />
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- ON-CHAIN TRANSPARENCY ---------------- */
function Transparency() {
  const ref = useReveal<HTMLDivElement>();
  const { t } = useI18n();

  const meal4Json = useMemo(
    () =>
      JSON.stringify(
        {
          name: "MEAL4 - The Inflation Shield",
          symbol: "MEAL4",
          description: "Fixed-supply food-security asset. 1,000,000 tokens maximum.",
          image: "https://mealtoken24.com/assets/meal4-token.png",
          attributes: [
            { trait_type: "Max Supply", value: "1,000,000" },
            { trait_type: "Freeze Authority", value: "REVOKED" },
            { trait_type: "Mint Authority", value: "DESTROYED" },
            { trait_type: "Decimals", value: 6 },
            { trait_type: "Mutable", value: false },
          ],
          properties: {
            category: "token",
            creators: [{ address: "MEAL4...Contract", verified: true }],
          },
        },
        null,
        2,
      ),
    [],
  );

  const meal2Json = useMemo(
    () =>
      JSON.stringify(
        {
          name: "MEAL2 - The Daily Utility",
          symbol: "MEAL2",
          description:
            "Utility token for instant mobile payments, loyalty, and sponsorships within the Sustainable Food & Eco Network.",
          image: "https://mealtoken24.com/assets/meal2-token.png",
          attributes: [
            { trait_type: "Type", value: "Utility / Payment" },
            { trait_type: "Freeze Authority", value: "REVOKED" },
            { trait_type: "Mint Authority", value: "RETAINED FOR NETWORK GROWTH" },
            { trait_type: "Decimals", value: 6 },
            { trait_type: "Mutable", value: true },
          ],
          properties: {
            category: "token",
            creators: [{ address: "MEAL2...Contract", verified: true }],
          },
        },
        null,
        2,
      ),
    [],
  );

  const downloadJson = (filename: string, content: string) => {
    const blob = new Blob([content], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section id="transparency" className="relative overflow-hidden py-24 sm:py-32">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(50% 50% at 20% 20%, oklch(0.97 0.13 120 / 0.10), transparent 60%), radial-gradient(50% 50% at 80% 80%, oklch(0.96 0.07 155 / 0.08), transparent 60%)",
        }}
      />
      <div ref={ref} className="reveal mx-auto max-w-7xl px-4 sm:px-6">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            {t("transparency.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("transparency.h.a")}<span className="italic text-gradient-saffron">{t("transparency.h.b")}</span>{t("transparency.h.c")}
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            {t("transparency.sub")}
          </p>
        </div>

        {/* Two-Column Dashboard */}
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {/* MEAL4 Column */}
          <div className="rounded-3xl glass-strong luminous-border p-7 sm:p-8 transition-shadow hover:shadow-[var(--shadow-elev)]">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl border border-saffron/30 bg-saffron/10 glow-saffron">
                <ShieldCheck className="h-5 w-5 text-saffron" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight">
                  {t("verify.meal4.title")}
                </h3>
                <p className="text-xs text-muted-foreground">{t("verify.meal4.sub")}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-mint/30 bg-mint/10 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-mint">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t("verify.freeze")}
              </span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/30 bg-rose-400/10 px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                style={{ color: "#f87171" }}
              >
                <Lock className="h-3.5 w-3.5" />
                {t("verify.mint.destroyed")}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://solscan.io"
                target="_blank"
                rel="noopener noreferrer"
                className="liquid-btn inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.06]"
              >
                <ExternalLink className="h-4 w-4 text-saffron" />
                {t("verify.meal4.cta")}
              </a>
              <button
                type="button"
                onClick={() => downloadJson("MEAL4-metadata.json", meal4Json)}
                className="liquid-btn inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.06]"
              >
                <Download className="h-4 w-4 text-mint" />
                {t("verify.meal4.download")}
              </button>
            </div>
          </div>

          {/* MEAL2 Column */}
          <div className="rounded-3xl glass-strong luminous-border p-7 sm:p-8 transition-shadow hover:shadow-[var(--shadow-elev)]">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl border border-mint/30 bg-mint/10 glow-mint">
                <Activity className="h-5 w-5 text-mint" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight">
                  {t("verify.meal2.title")}
                </h3>
                <p className="text-xs text-muted-foreground">{t("verify.meal2.sub")}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-mint/30 bg-mint/10 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-mint">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t("verify.freeze")}
              </span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                style={{ color: "#fbbf24" }}
              >
                <Sparkle className="h-3.5 w-3.5" />
                {t("verify.mint.retained")}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://solscan.io"
                target="_blank"
                rel="noopener noreferrer"
                className="liquid-btn inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.06]"
              >
                <ExternalLink className="h-4 w-4 text-mint" />
                {t("verify.meal2.cta")}
              </a>
              <button
                type="button"
                onClick={() => downloadJson("MEAL2-metadata.json", meal2Json)}
                className="liquid-btn inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.06]"
              >
                <Download className="h-4 w-4 text-saffron" />
                {t("verify.meal2.download")}
              </button>
            </div>
          </div>
        </div>

        {/* Verification Checklist Panel */}
        <VerificationChecklist />

        {/* Visual Anchor — Code Snippet */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-black/40 p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-saffron" />
              {t("transparency.jsonStructure")}
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              .json
            </span>
          </div>
          <pre className="mt-4 overflow-x-auto text-xs leading-relaxed sm:text-sm">
            <code className="font-mono text-foreground/80">
              <span style={{ color: "#f87171" }}>&quot;name&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#a3e635" }}>&quot;MEAL4 - The Inflation Shield&quot;</span>
              <span style={{ color: "#94a3b8" }}>,</span>
              {"\n"}
              <span style={{ color: "#f87171" }}>&quot;symbol&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#a3e635" }}>&quot;MEAL4&quot;</span>
              <span style={{ color: "#94a3b8" }}>,</span>
              {"\n"}
              <span style={{ color: "#f87171" }}>&quot;uri&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#a3e635" }}>
                &quot;https://mealtoken24.com/metadata/meal4.json&quot;
              </span>
              <span style={{ color: "#94a3b8" }}>,</span>
              {"\n"}
              <span style={{ color: "#f87171" }}>&quot;seller_fee_basis_points&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#fbbf24" }}>0</span>
              <span style={{ color: "#94a3b8" }}>,</span>
              {"\n"}
              <span style={{ color: "#f87171" }}>&quot;creators&quot;</span>
              <span style={{ color: "#94a3b8" }}>: [</span>
              {"\n"}
              <span style={{ color: "#94a3b8" }}> {"{ "}</span>
              <span style={{ color: "#f87171" }}>&quot;address&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#a3e635" }}>&quot;SFN...Contract&quot;</span>
              <span style={{ color: "#94a3b8" }}>, </span>
              <span style={{ color: "#f87171" }}>&quot;verified&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#38bdf8" }}>true</span>
              <span style={{ color: "#94a3b8" }}>, </span>
              <span style={{ color: "#f87171" }}>&quot;share&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#fbbf24" }}>100</span>
              <span style={{ color: "#94a3b8" }}> {"}"}</span>
              {"\n"}
              <span style={{ color: "#94a3b8" }}>],</span>
              {"\n"}
              <span style={{ color: "#f87171" }}>&quot;mutable&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#f87171" }}>false</span>
              <span style={{ color: "#94a3b8" }}>, </span>
              <span style={{ color: "#64748b" }}>{t("transparency.jsonImmutable")}</span>
              {"\n"}
              <span style={{ color: "#f87171" }}>&quot;max_supply&quot;</span>
              <span style={{ color: "#94a3b8" }}>: </span>
              <span style={{ color: "#fbbf24" }}>1_000_000</span>
              <span style={{ color: "#94a3b8" }}>, </span>
              <span style={{ color: "#64748b" }}>{t("transparency.jsonCeiling")}</span>
            </code>
          </pre>
        </div>
      </div>
    </section>
  );
}

/* ---------------- VERIFICATION CHECKLIST PANEL ----------------
 * PRIVACY GUARDRAIL (do not remove):
 * This panel is strictly contract-level. All Solana RPC traffic flows
 * through `@/lib/solana-rpc`, which enforces a runtime allow-list and
 * blocks any wallet-tracking method (getTokenAccountsByOwner,
 * getProgramAccounts, getTokenAccountBalance, etc.) with a console
 * SECURITY ALERT. Do NOT introduce input fields, dropdowns, or any UI
 * surface accepting a user-supplied public key / wallet address. SFN
 * never tracks individual wallets. Any future automated UI update
 * reintroducing such a feature must be rejected at review.
 * ---------------------------------------------------------------- */

const MEAL4_MINT_ADDRESS = "61wBjt2Hcw7byU6RM2tetzpvPvwccmmnqTuD44yEdufa";
const MEAL2_MINT_ADDRESS = "Dog52jMCXacoyzE9jgD3raRuksLKUqv35x6QWRTe6NTK";

type ChecklistItem = {
  label: string;
  status: string;
  tone: "green" | "amber";
};

function formatSupply(supply: string, decimals: number): string {
  try {
    const whole = decimals === 0 ? BigInt(supply) : BigInt(supply) / BigInt(10) ** BigInt(decimals);
    return whole.toLocaleString("en-US");
  } catch {
    return supply;
  }
}

// Exponential backoff: 500ms -> 1000ms -> 2000ms. Up to 3 attempts total.
async function fetchWithRetry<T>(
  task: (attemptSignal: AbortSignal) => Promise<T>,
  options: {
    maxAttempts?: number;
    initialDelayMs?: number;
    perAttemptTimeoutMs?: number;
    onRetry?: (attempt: number) => void;
  } = {},
): Promise<T> {
  const { maxAttempts = 3, initialDelayMs = 500, perAttemptTimeoutMs = 3000, onRetry } = options;
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const attemptController = new AbortController();
    const timeout = setTimeout(() => attemptController.abort(), perAttemptTimeoutMs);
    try {
      return await task(attemptController.signal);
    } catch (err) {
      lastError = err;
      if (attempt === maxAttempts) break;
      const delay = initialDelayMs * 2 ** (attempt - 1);
      onRetry?.(attempt + 1);
      await new Promise((r) => setTimeout(r, delay));
    } finally {
      clearTimeout(timeout);
    }
  }
  throw lastError instanceof Error ? lastError : new Error("RPC retry exhausted");
}

export function VerificationChecklist() {
  const { t } = useI18n();
  const [scanning, setScanning] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [cached, setCached] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [scanTrigger, setScanTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setScanning(true);
      setRevealed(false);
      setItems([]);
      setCached(false);
      setRetryAttempt(0);

      try {
        const onRetry = (attempt: number) => {
          if (!cancelled) setRetryAttempt(attempt);
        };
        const [meal4, meal2] = await Promise.all([
          fetchWithRetry((signal) => fetchMintInfo(MEAL4_MINT_ADDRESS, signal), { onRetry }),
          fetchWithRetry((signal) => fetchMintInfo(MEAL2_MINT_ADDRESS, signal), { onRetry }),
        ]);
        if (cancelled) return;

        const next: ChecklistItem[] = [
          {
            label: t("verify.item.meal4Supply.label"),
            status: `${t("verify.item.meal4Supply.statusLive")}${formatSupply(meal4.supply, meal4.decimals)}`,
            tone: "green",
          },
          {
            label: t("verify.item.meal4Mint.label"),
            status:
              meal4.mintAuthority === null
                ? t("verify.item.meal4Mint.statusPassed")
                : t("verify.item.meal4Mint.statusActive"),
            tone: meal4.mintAuthority === null ? "green" : "amber",
          },
          {
            label: t("verify.item.meal4Freeze.label"),
            status:
              meal4.freezeAuthority === null
                ? t("verify.item.meal4Freeze.statusPassed")
                : t("verify.item.meal4Freeze.statusActive"),
            tone: meal4.freezeAuthority === null ? "green" : "amber",
          },
          {
            label: t("verify.item.meal2Freeze.label"),
            status:
              meal2.freezeAuthority === null
                ? t("verify.item.meal2Freeze.statusPassed")
                : t("verify.item.meal2Freeze.statusActive"),
            tone: meal2.freezeAuthority === null ? "green" : "amber",
          },
          {
            label: t("verify.item.meal2Mint.label"),
            status:
              meal2.mintAuthority === null
                ? t("verify.item.meal2Mint.statusRevoked")
                : t("verify.item.meal2Mint.statusActive"),
            tone: meal2.mintAuthority === null ? "green" : "amber",
          },
          {
            label: t("verify.item.meal2Supply.label"),
            status: `${t("verify.item.meal2Supply.statusLive")}${formatSupply(meal2.supply, meal2.decimals)} MEAL2`,
            tone: "green",
          },
        ];
        setItems(next);
        setCached(false);
      } catch {
        // Graceful fallback: verified genesis snapshot.
        setItems([
          {
            label: t("verify.item.meal4Supply.label"),
            status: t("verify.item.meal4Supply.statusPassed"),
            tone: "green",
          },
          {
            label: t("verify.item.meal4Mint.label"),
            status: t("verify.item.meal4Mint.statusPassed"),
            tone: "green",
          },
          {
            label: t("verify.item.meal4Freeze.label"),
            status: t("verify.item.meal4Freeze.statusPassed"),
            tone: "green",
          },
          {
            label: t("verify.item.meal2Freeze.label"),
            status: t("verify.item.meal2Freeze.statusPassed"),
            tone: "green",
          },
          {
            label: t("verify.item.meal2Mint.label"),
            status: t("verify.item.meal2Mint.statusActive"),
            tone: "amber",
          },
        ]);
        setCached(true);
      } finally {
        if (!cancelled) {
          setScanning(false);
          setTimeout(() => setRevealed(true), 80);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scanTrigger, t]);

  const handleRetry = () => setScanTrigger((n) => n + 1);

  return (
    <div
      className="relative mx-auto mt-10 max-w-3xl rounded-3xl border p-7 sm:p-8"
      style={{
        borderColor: "rgba(222, 255, 154, 0.15)",
        background: "linear-gradient(180deg, rgba(222,255,154,0.03), rgba(0,0,0,0.4))",
        boxShadow: "0 0 60px rgba(222,255,154,0.06), 0 30px 80px -30px rgba(0,0,0,0.6)",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div
            className="grid h-9 w-9 place-items-center rounded-xl"
            style={{
              border: "1px solid rgba(222,255,154,0.25)",
              background: "rgba(222,255,154,0.08)",
            }}
          >
            <ShieldCheck className="h-4 w-4 text-saffron" strokeWidth={1.5} />
          </div>
          <div>
            <h4 className="font-display text-base font-semibold tracking-tight">
              {t("verify.scanTitle")}
            </h4>
            <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              {t("verify.mainnetRealtime")}
            </p>
          </div>
        </div>
        {scanning ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-saffron/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-saffron">
            <Loader className="h-3 w-3 animate-spin" />
            {t("verify.badge.connecting")}
          </span>
        ) : cached ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-amber-300">
            <ShieldCheck className="h-3 w-3" />
            {t("verify.badge.cached")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-mint/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-mint">
            <CheckCircle2 className="h-3 w-3" />
            {t("verify.badge.verified")}
          </span>
        )}
      </div>

      {scanning && (
        <div className="mt-8 flex flex-col items-center justify-center py-6">
          <Loader className="h-8 w-8 animate-spin text-saffron" strokeWidth={1.5} />
          <p className="mt-3 text-sm font-mono uppercase tracking-[0.16em] text-muted-foreground">
            {t("verify.connecting")}
          </p>
          {retryAttempt > 1 && (
            <p className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300/70">
              {t("verify.networkBusy")}{retryAttempt}/3)
            </p>
          )}
          <div className="mt-4 h-1 w-48 overflow-hidden rounded-full bg-white/5">
            <div
              className="h-full rounded-full bg-gradient-to-r from-saffron to-mint"
              style={{
                width: "100%",
                animation: "progress-scan 1.8s ease-in-out forwards",
              }}
            />
          </div>
        </div>
      )}

      {!scanning && (
        <div className="mt-6 space-y-3">
          {items.map((item, i) => {
            const isGreen = item.tone === "green";
            const delay = `${i * 90}ms`;
            return (
              <div
                key={item.label}
                className="flex items-center justify-between rounded-xl border px-4 py-3 transition-all duration-500"
                style={{
                  borderColor: isGreen ? "rgba(74,222,128,0.12)" : "rgba(251,191,36,0.12)",
                  background: isGreen ? "rgba(74,222,128,0.03)" : "rgba(251,191,36,0.03)",
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? "translateY(0)" : "translateY(8px)",
                  transitionDelay: delay,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="grid h-7 w-7 place-items-center rounded-full"
                    style={{
                      background: isGreen ? "rgba(74,222,128,0.12)" : "rgba(251,191,36,0.12)",
                    }}
                  >
                    {isGreen ? (
                      <CheckCircle2
                        className="h-3.5 w-3.5"
                        style={{ color: "#4ade80" }}
                        strokeWidth={2.5}
                      />
                    ) : (
                      <Sparkle
                        className="h-3.5 w-3.5"
                        style={{ color: "#fbbf24" }}
                        strokeWidth={2.5}
                      />
                    )}
                  </div>
                  <span className="text-sm font-medium text-foreground/90">{item.label}</span>
                </div>
                <span
                  className="text-right text-xs font-mono uppercase tracking-wider"
                  style={{ color: isGreen ? "#4ade80" : "#fbbf24" }}
                >
                  {item.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {!scanning && (
        <div
          className="mt-5 flex flex-col items-center justify-center gap-1 border-t pt-4"
          style={{
            borderColor: "rgba(255,255,255,0.06)",
            opacity: revealed ? 1 : 0,
            transform: revealed ? "translateY(0)" : "translateY(6px)",
            transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
            transitionDelay: "720ms",
          }}
        >
          <div className="flex items-center gap-2">
            <Lock className="h-3 w-3 text-muted-foreground" strokeWidth={2} />
            <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
              {t("verify.cryptoSecured")}
            </span>
          </div>
          {cached && (
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300/70">
              {t("verify.genesisSnapshot")}
            </span>
          )}
        </div>
      )}

      {/* Retry Blockchain Scan button */}
      <div className="mt-5 flex justify-center">
        <button
          type="button"
          onClick={handleRetry}
          disabled={scanning}
          className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            borderColor: scanning ? "rgba(222,255,154,0.12)" : "rgba(222,255,154,0.35)",
            color: scanning ? "rgba(222,255,154,0.4)" : "#DEFF9A",
            background: scanning ? "transparent" : "rgba(222,255,154,0.06)",
          }}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t("verify.retryScan")}
        </button>
      </div>

      {/* Privacy Notice — contract-level only, no wallet tracking */}
      <div
        className="mt-6 flex items-start gap-2.5 rounded-xl border px-4 py-3"
        style={{
          borderColor: "rgba(255,255,255,0.06)",
          background: "rgba(255,255,255,0.015)",
        }}
      >
        <Lock className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={2} />
        <p className="text-[11px] leading-relaxed text-muted-foreground/85">
          <span className="font-mono uppercase tracking-[0.14em] text-foreground/70">
            {t("verify.privacyLabel")}
          </span>{" "}
          {t("verify.privacyText")}
        </p>
      </div>
    </div>
  );
}
type DonutSegment = { key: string; label: string; value: number; color: string };

function EcosystemFlow({
  activeKey,
  segments,
  headerTitle,
  supplyLabel,
  accent,
  centerAllocLabel,
  chartSummaryLabel,
  chartHint,
  legendAriaLabel,
  segmentSrTemplate,
  segmentHint,
  reduceMotion = false,
  onSelect,
  onActivate,
}: {
  activeKey: string;
  segments: DonutSegment[];
  headerTitle: string;
  supplyLabel: string;
  accent: string;
  centerAllocLabel: string;
  chartSummaryLabel?: string;
  chartHint?: string;
  legendAriaLabel?: string;
  segmentSrTemplate?: string;
  segmentHint?: string;
  reduceMotion?: boolean;
  onSelect?: (key: string) => void;
  onActivate?: (key: string) => void;
}) {
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const r = 112;
  const strokeW = 36;
  const C = 2 * Math.PI * r;
  let cumulative = 0;
  const activeSeg = segments.find((s) => s.key === activeKey);
  const activeIndex = Math.max(
    0,
    segments.findIndex((s) => s.key === activeKey),
  );
  const transition = reduceMotion ? "none" : "stroke-width 0.4s, filter 0.4s, opacity 0.4s";

  const moveBy = (delta: number) => {
    if (!onSelect || segments.length === 0) return;
    const next = (activeIndex + delta + segments.length) % segments.length;
    onSelect(segments[next].key);
  };

  const onLegendKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, key: string) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      moveBy(1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      moveBy(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      onSelect?.(segments[0].key);
    } else if (e.key === "End") {
      e.preventDefault();
      onSelect?.(segments[segments.length - 1].key);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate?.(key);
    }
  };

  const fmtSeg = (label: string, value: number) =>
    (segmentSrTemplate ?? "{label}: {value}%")
      .replace("{label}", label)
      .replace("{value}", String(value));

  const summary = segments.map((s) => fmtSeg(s.label, s.value)).join(" ");

  return (
    <div className="relative w-full max-w-[440px] mx-auto rounded-3xl glass-strong overflow-hidden p-5 sm:p-7">
      {/* header: grid keeps title left-truncated and supply badge intact on mobile */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <span className="min-w-0 truncate">{headerTitle}</span>
        <span className="inline-flex items-center gap-1.5 shrink-0">
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: accent, boxShadow: `0 0 10px ${accent}` }}
          />
          <span className="truncate max-w-[10rem] sm:max-w-none">{supplyLabel}</span>
        </span>
      </div>

      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="relative mx-auto mt-4 block aspect-square w-full max-w-[320px]"
        role="img"
        aria-label={`${headerTitle}. ${chartSummaryLabel ?? ""} ${summary} ${chartHint ?? ""}`.trim()}
      >
        {/* base ring */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={strokeW}
        />
        {segments.map((seg) => {
          const dash = (seg.value / 100) * C;
          const gap = C - dash;
          const offset = -((cumulative / 100) * C);
          cumulative += seg.value;
          const isActive = activeKey === seg.key;
          return (
            <circle
              key={seg.key}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={isActive ? strokeW + 4 : strokeW}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${cx} ${cy})`}
              onMouseEnter={() => onSelect?.(seg.key)}
              onClick={() => onActivate?.(seg.key)}
              role={onActivate ? "button" : undefined}
              tabIndex={onActivate ? -1 : undefined}
              aria-label={`${fmtSeg(seg.label, seg.value)} ${segmentHint ?? ""}`.trim()}
              style={{
                cursor: onActivate ? "pointer" : undefined,
                transition,
                opacity: isActive ? 1 : 0.78,
                filter: isActive && !reduceMotion ? `drop-shadow(0 0 14px ${seg.color}aa)` : "none",
              }}
            >
              <title>{fmtSeg(seg.label, seg.value)}</title>
            </circle>
          );
        })}
        {/* center labels */}
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fontFamily="Urbanist"
          fontSize="40"
          fontWeight="700"
          fill="white"
        >
          {activeSeg ? `${activeSeg.value}%` : "100%"}
        </text>
        <text
          x={cx}
          y={cy + 18}
          textAnchor="middle"
          fontFamily="JetBrains Mono"
          fontSize="9"
          letterSpacing="3"
          fill="rgba(255,255,255,0.55)"
        >
          {activeSeg ? activeSeg.label.toUpperCase().slice(0, 28) : centerAllocLabel}
        </text>
      </svg>

      {/* legend — keyboard-navigable radiogroup */}
      <div
        role="radiogroup"
        aria-label={legendAriaLabel ?? "Distribution segments"}
        className="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 font-mono text-[10px] uppercase tracking-[0.16em] sm:grid-cols-2"
      >
        {segments.map((seg) => {
          const isActive = activeKey === seg.key;
          return (
            <div
              key={seg.key}
              role="radio"
              aria-checked={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => {
                onSelect?.(seg.key);
                onActivate?.(seg.key);
              }}
              onMouseEnter={() => onSelect?.(seg.key)}
              onKeyDown={(e) => onLegendKeyDown(e, seg.key)}
              aria-label={`${fmtSeg(seg.label, seg.value)} ${segmentHint ?? ""}`.trim()}
              className="group flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-white/60 cursor-pointer hover:bg-white/[0.04] hover:scale-[1.02]"
              style={{ opacity: isActive ? 1 : 0.6 }}
            >
              <span
                aria-hidden
                className="h-2 w-2 shrink-0 rounded-sm transition-shadow"
                style={{
                  background: seg.color,
                  boxShadow: isActive ? `0 0 8px ${seg.color}` : "none",
                }}
              />
              <span className="min-w-0 flex-1 truncate text-foreground/80">
                {seg.value}% {seg.label}
              </span>
              <Info
                aria-hidden
                className="h-3 w-3 shrink-0 text-muted-foreground/60 transition-all duration-200 group-hover:text-foreground group-focus-visible:text-foreground"
                style={{
                  color: isActive ? seg.color : undefined,
                  filter:
                    isActive && !reduceMotion ? `drop-shadow(0 0 4px ${seg.color})` : undefined,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- CONNECTION FLOW: From Digital Token to Physical Plate ---------------- */
function ConnectionFlow() {
  const { t } = useI18n();
  const ref = useReveal<HTMLDivElement>();
  const steps = [
    {
      n: "01",
      title: t("connection.step1.title"),
      text: t("connection.step1.text"),
      Icon: Coins,
    },
    {
      n: "02",
      title: t("connection.step2.title"),
      text: t("connection.step2.text"),
      Icon: Activity,
    },
    {
      n: "03",
      title: t("connection.step3.title"),
      text: t("connection.step3.text"),
      Icon: Utensils,
    },
  ];
  return (
    <section id="connection" className="relative py-24 sm:py-32">
      <div ref={ref} className="reveal mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span
            className="font-mono text-[11px] font-medium uppercase tracking-[0.22em]"
            style={{ color: "#DEFF9A" }}
          >
            {t("connection.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("connection.title")}
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            {t("connection.sub")}
          </p>
        </div>

        <div className="relative mt-16 grid gap-6 md:grid-cols-3">
          {/* connecting line (desktop) */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 right-0 top-[72px] hidden md:block"
            style={{
              height: "1px",
              background:
                "linear-gradient(90deg, transparent 8%, rgba(222,255,154,0.4) 30%, rgba(218,255,222,0.4) 70%, transparent 92%)",
            }}
          />
          {steps.map((s, i) => (
            <div
              key={s.n}
              className="relative rounded-3xl glass p-7 sm:p-8 transition-all hover:-translate-y-1"
              style={{ borderColor: "rgba(255,255,255,0.1)" }}
            >
              {/* step badge sits on the line */}
              <div className="relative z-10 flex items-center justify-between">
                <div
                  className="grid h-14 w-14 place-items-center rounded-2xl"
                  style={{
                    background: "rgba(0,0,0,0.6)",
                    border: "1px solid rgba(222,255,154,0.35)",
                    boxShadow: "0 0 24px rgba(222,255,154,0.18)",
                  }}
                >
                  <s.Icon className="h-6 w-6" style={{ color: "#DEFF9A", strokeWidth: 1.5 }} />
                </div>
                <span className="font-mono text-xs tracking-[0.22em] text-muted-foreground">
                  {s.n}
                </span>
              </div>
              <h3 className="mt-7 font-display text-2xl font-semibold tracking-tight">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              {i < steps.length - 1 && (
                <ArrowRight
                  aria-hidden
                  className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 md:block"
                  style={{ color: "rgba(222,255,154,0.5)" }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TokenoCard({
  metric,
  isActive,
  delay,
  onHover,
  onClick,
}: {
  metric: TokenoMetric;
  isActive: boolean;
  delay: number;
  onHover: () => void;
  onClick: () => void;
}) {
  const tiltRef = useTilt<HTMLDivElement>(10);
  const Icon = metric.icon;
  const color = TONE_COLOR[metric.tone];
  return (
    <div
      ref={tiltRef}
      onMouseEnter={onHover}
      onFocus={onHover}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open details for ${metric.label}`}
      aria-haspopup="dialog"
      className={`tilt-card group relative reveal reveal-delay-${(delay % 4) + 1} cursor-pointer rounded-2xl glass-strong luminous-border p-5 transition-shadow duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian focus-visible:ring-white/60`}
      style={{
        boxShadow: isActive
          ? `0 30px 80px -40px ${color}, 0 0 0 1px oklch(1 0 0 / 0.08) inset`
          : undefined,
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: `radial-gradient(120% 120% at var(--mx,50%) var(--my,50%), ${color}33, transparent 55%)`,
        }}
      />
      <div className="relative flex items-start justify-between">
        <div className="grid h-10 w-10 place-items-center rounded-xl glass luminous-border">
          <Icon className="h-5 w-5" />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color }}>
          {metric.tone}
        </span>
      </div>
      <div
        className="relative mt-5 font-display text-4xl font-medium tracking-tight"
        style={{ color }}
      >
        {metric.value}
      </div>
      <div className="relative mt-1 text-sm font-semibold text-foreground">{metric.label}</div>
      <p className="relative mt-2 text-xs leading-relaxed text-muted-foreground">{metric.desc}</p>

      {/* mini sparkline */}
      <svg viewBox="0 0 120 24" className="relative mt-4 h-6 w-full" aria-hidden>
        <path
          d="M0,18 L15,12 L28,15 L42,8 L58,11 L74,5 L90,9 L105,3 L120,7"
          fill="none"
          stroke={color}
          strokeOpacity="0.8"
          strokeWidth="1.4"
        />
        <path
          d="M0,18 L15,12 L28,15 L42,8 L58,11 L74,5 L90,9 L105,3 L120,7 L120,24 L0,24 Z"
          fill={color}
          fillOpacity="0.08"
        />
      </svg>
    </div>
  );
}

/* ---------------- LEGAL MODAL ---------------- */
type LegalKind = "privacy" | "terms" | "imprint";
const LEGAL_CONTENT: Record<LegalKind, { title: string; body: string[] }> = {
  privacy: {
    title: "Privacy Policy",
    body: [
      "SFN | MealToken24 respects your privacy. This placeholder Privacy Policy describes the data we collect, why we collect it, and how we protect it.",
      "We process only information necessary to operate the Sustainable Food & Eco Network — contact form submissions, wallet addresses you voluntarily connect, and aggregated, anonymized usage analytics.",
      "We do not sell personal data. Cookies are limited to what is essential for the site to function. You may request deletion of your data at any time via the contact form.",
      "This document is a placeholder; the final policy will be reviewed by counsel before public launch.",
    ],
  },
  terms: {
    title: "Terms of Service",
    body: [
      "By accessing SFN | MealToken24 you agree to use the platform for its stated purpose: participating in the Sustainable Food & Eco Network ecosystem.",
      "MEAL2 and MEAL4 are utility and asset-backed community tokens designed for practical use within partner kitchens. They are not financial investments or securities.",
      "You are responsible for the security of your own wallet and private keys. SFN cannot recover lost keys or reverse on-chain transactions.",
      "These terms are a placeholder pending final legal review.",
    ],
  },
  imprint: {
    title: "Legal Notice (Imprint)",
    body: [
      "Information in accordance with applicable disclosure requirements.",
      "Operator: SFN — Sustainable Food & Eco Network",
      "Live Pilot Proof of Concept: Thailand Sandbox",
      "Contact: via the contact form on this site.",
      "Responsible for content: SFN Founding Team. This imprint is a placeholder and will be replaced with the final legal entity details prior to public launch.",
    ],
  },
};

function LegalLink({ kind, label }: { kind: LegalKind; label: string }) {
  const [open, setOpen] = useState(false);
  const content = LEGAL_CONTENT[kind];
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useModalA11y({
    open,
    onClose: () => setOpen(false),
    analyticsId: `legal_${kind}`,
    initialFocusRef: closeRef,
  });
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded"
      >
        {label}
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={`legal-${kind}-title`}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" aria-hidden />
          <div
            ref={dialogRef}
            tabIndex={-1}
            className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-3xl glass-strong p-8 sm:p-10 focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              ref={closeRef}
              type="button"
              onClick={() => setOpen(false)}
              aria-label={`Close ${label}`}
              className="absolute right-5 top-5 rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              ✕
            </button>
            <h3
              id={`legal-${kind}-title`}
              className="font-display text-2xl font-medium tracking-tight"
            >
              {content.title}
            </h3>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-muted-foreground">
              {content.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- FOOTER ---------------- */
function Footer() {
  const { t } = useI18n();
  const footerRef = useRef<HTMLElement | null>(null);
  const wordmarkRef = useRef<HTMLDivElement | null>(null);
  const bubbleRef = useRef<HTMLButtonElement | null>(null);
  const [bubbleVisible, setBubbleVisible] = useState(false);

  // Horizontal parallax of the giant wordmark, tied to scroll position
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = footerRef.current;
        const wm = wordmarkRef.current;
        if (!el || !wm) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        // Progress: 0 when footer enters viewport bottom, 1 when fully passed
        const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
        const offset = -progress * 320; // px horizontal drift
        wm.style.transform = `translate3d(${offset}px, 0, 0)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Liquid bubble: organically follows cursor inside footer zone with easing
  useEffect(() => {
    const el = footerRef.current;
    const bubble = bubbleRef.current;
    if (!el || !bubble) return;

    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    let raf = 0;
    let inside = false;

    const rectOf = () => el.getBoundingClientRect();

    const animate = () => {
      curX += (targetX - curX) * 0.12;
      curY += (targetY - curY) * 0.12;
      const squish = Math.min(20, Math.hypot(targetX - curX, targetY - curY));
      bubble.style.transform = `translate3d(${curX}px, ${curY}px, 0) translate(-50%, -50%) scale(${1 + squish * 0.004}, ${1 - squish * 0.003})`;
      if (inside || Math.abs(targetX - curX) > 0.3 || Math.abs(targetY - curY) > 0.3) {
        raf = requestAnimationFrame(animate);
      }
    };

    const onMove = (e: MouseEvent) => {
      const r = rectOf();
      targetX = e.clientX - r.left;
      targetY = e.clientY - r.top;
      if (!inside) {
        inside = true;
        curX = targetX;
        curY = targetY;
        setBubbleVisible(true);
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(animate);
      }
    };
    const onLeave = () => {
      inside = false;
      setBubbleVisible(false);
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  const backToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer
      ref={footerRef}
      className="relative isolate overflow-hidden border-t border-white/10 pt-24 sm:pt-32"
    >
      {/* Ambient gradient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(60% 80% at 15% 0%, oklch(0.82 0.18 75 / 0.18), transparent 60%), radial-gradient(60% 80% at 85% 20%, oklch(0.86 0.16 165 / 0.14), transparent 65%), linear-gradient(180deg, transparent, oklch(0.09 0.01 250) 70%)",
        }}
      />

      {/* Monumental parallax wordmark */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-[-2vw] -z-10 select-none overflow-hidden"
      >
        <div
          ref={wordmarkRef}
          className="whitespace-nowrap font-display font-black leading-[0.78] tracking-[-0.04em] will-change-transform"
          style={{
            fontSize: "clamp(7rem, 22vw, 22rem)",
            background:
              "linear-gradient(180deg, oklch(1 0 0 / 0.06) 0%, oklch(0.82 0.18 75 / 0.08) 55%, transparent 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            WebkitTextStroke: "1px oklch(1 0 0 / 0.06)",
          }}
        >
          {t("footer.marquee")}
        </div>
      </div>

      {/* Editorial content */}
      <div className="relative mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-saffron to-coral glow-saffron">
                <Coins className="h-4 w-4 text-obsidian" />
              </span>
              <span className="font-display text-xl font-bold tracking-tight">Meal/Tokens</span>
            </div>
            <p className="mt-4 max-w-md text-base text-muted-foreground">{t("footer.tag")}</p>
          </div>
          <div className="md:col-span-7 md:flex md:justify-end">
            <nav className="grid grid-cols-2 gap-x-10 gap-y-3 font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground sm:grid-cols-3">
              <a
                href="/vision"
                className="group inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
              >
                {t("nav.vision")}{" "}
                <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href="#about"
                className="group inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
              >
                {t("footer.about")}{" "}
                <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href="#contact"
                className="group inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
              >
                {t("footer.contact")}{" "}
                <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href="#docs"
                className="group inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
              >
                {t("footer.legal")}{" "}
                <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </nav>
          </div>
        </div>

        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-muted-foreground">
          {t("footer.disclaimer")}
        </p>
        {/* Trust & verification resources — audit, legal, treasury */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Lock,
              label: "Security Audit Report",
              meta: "PDF · DE / EN",
              href: "/docs/cryptographic-security-audit-report.pdf",
              file: "cryptographic-security-audit-report.pdf",
            },
            {
              icon: ShieldCheck,
              label: "Thailand Legal Framework",
              meta: "PDF · DE / EN",
              href: "/docs/thailand-gastronomy-legal-framework.pdf",
              file: "thailand-gastronomy-legal-framework.pdf",
            },
            {
              icon: FileText,
              label: "Master Whitepaper v1.0",
              meta: "PDF · EN",
              href: "/docs/SFN_Network_Master_Whitepaper_v1.0.pdf",
              file: "SFN_Network_Master_Whitepaper_v1.0.pdf",
            },
            {
              icon: Activity,
              label: "Treasury on Solscan",
              meta: shortTreasury(SFN_TREASURY_ADDRESS),
              href: `https://solscan.io/account/${SFN_TREASURY_ADDRESS}`,
              file: null as string | null,
            },
          ].map((d) => (
            <a
              key={d.label}
              href={d.href}
              {...(d.file
                ? { download: d.file, rel: "noopener" }
                : { target: "_blank", rel: "noopener noreferrer" })}
              onClick={() =>
                track(d.file ? "doc_download" : "cta_click", {
                  id: d.label,
                  file: d.file ?? undefined,
                  surface: "footer_trust",
                })
              }
              className="group flex items-center gap-3 rounded-2xl glass luminous-border px-4 py-3 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[var(--shadow-elev)]"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-saffron/20 to-coral/20 text-saffron">
                <d.icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-foreground transition-colors group-hover:text-saffron">
                  {d.label}
                </span>
                <span className="block truncate font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {d.meta}
                </span>
              </span>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <LegalLink kind="privacy" label="Privacy Policy" />
          <LegalLink kind="terms" label="Terms of Service" />
          <LegalLink kind="imprint" label="Legal Notice (Imprint)" />
        </div>

        <div className="mt-10">
          <EcosystemHub activeId="sfn" />
        </div>
        <div className="mt-6 flex flex-col items-center gap-4 border-t border-white/5 pt-6">
          <SocialIcons surface="footer" />
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t("credits.line")}
          </p>
          <div className="flex w-full items-center justify-between gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              © {new Date().getFullYear()} · {t("footer.rights")}
            </p>
            <p className="hidden font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground sm:block">
              DE · EN
            </p>
          </div>
        </div>
      </div>

      {/* Liquid metal back-to-top bubble */}
      <button
        ref={bubbleRef}
        onClick={backToTop}
        aria-label="Back to top"
        className={`pointer-events-auto absolute left-0 top-0 z-20 grid h-16 w-16 place-items-center rounded-full text-obsidian transition-opacity duration-500 ${
          bubbleVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background:
            "radial-gradient(circle at 30% 25%, oklch(1 0 0 / 0.9), oklch(0.82 0.18 75 / 0.95) 45%, oklch(0.74 0.19 30 / 0.95) 100%)",
          boxShadow:
            "0 0 0 1px oklch(1 0 0 / 0.25) inset, 0 0 40px oklch(0.82 0.18 75 / 0.55), 0 18px 50px -10px oklch(0 0 0 / 0.6)",
          backdropFilter: "blur(8px)",
          willChange: "transform",
        }}
      >
        <span
          aria-hidden
          className="absolute left-3 top-2 h-3 w-5 rounded-full bg-white/60 blur-[2px]"
        />
        <ArrowUpRight className="relative h-5 w-5 -rotate-45" strokeWidth={2.5} />
      </button>
    </footer>
  );
}

/* ---------------- PAGE ---------------- */
/* ---------------- LIVE BAROMETER ---------------- */
function Barometer() {
  const ref = useReveal<HTMLDivElement>();
  const { t } = useI18n();
  const items = [
    {
      icon: Wheat,
      label: t("barometer.rice"),
      value: 92,
      unit: "%",
      note: t("barometer.rice.note"),
      tone: "saffron" as const,
    },
    {
      icon: Droplets,
      label: t("barometer.oil"),
      value: 85,
      unit: "%",
      note: t("barometer.oil.note"),
      tone: "mint" as const,
    },
  ];
  return (
    <section id="barometer" className="relative overflow-hidden py-24 sm:py-32">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(50% 50% at 80% 20%, oklch(0.97 0.13 120 / 0.12), transparent 60%), radial-gradient(50% 50% at 20% 80%, oklch(0.96 0.07 155 / 0.10), transparent 60%)",
        }}
      />
      <div ref={ref} className="reveal mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            // {t("barometer.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("barometer.title.a")}{" "}
            <span className="italic text-gradient-saffron">{t("barometer.title.b")}</span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">{t("barometer.sub")}</p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {items.map((it) => {
            const isMint = it.tone === "mint";
            return (
              <div
                key={it.label}
                className="rounded-3xl glass-strong luminous-border p-7 shadow-[var(--shadow-elev)]"
              >
                <div className="flex items-center justify-between">
                  <div
                    className={`grid h-11 w-11 place-items-center rounded-xl border ${isMint ? "border-mint/30 bg-mint/10" : "border-saffron/30 bg-saffron/10"}`}
                  >
                    <it.icon
                      className={`h-5 w-5 ${isMint ? "text-mint" : "text-saffron"}`}
                      strokeWidth={1.5}
                    />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {it.note}
                  </span>
                </div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span
                    className={`font-display text-5xl font-bold ${isMint ? "text-gradient-mint" : "text-gradient-saffron"}`}
                  >
                    {it.value}
                  </span>
                  <span className="text-xl text-muted-foreground">{it.unit}</span>
                </div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {it.label}
                </div>
                <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-white/5">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: `${it.value}%`,
                      background: isMint ? "var(--gradient-mint)" : "var(--gradient-saffron)",
                      boxShadow: isMint ? "var(--glow-mint)" : "var(--glow-saffron)",
                    }}
                  />
                </div>
              </div>
            );
          })}

          {/* Solar status */}
          <div className="rounded-3xl glass-strong luminous-border p-7 shadow-[var(--shadow-elev)]">
            <div className="flex items-center justify-between">
              <div className="grid h-11 w-11 place-items-center rounded-xl border border-saffron/30 bg-saffron/10">
                <Sun className="h-5 w-5 text-saffron" strokeWidth={1.5} />
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-mint/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-mint">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-mint" />
                {t("barometer.live")}
              </span>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-5xl font-bold text-gradient-saffron">
                {t("barometer.active")}
              </span>
            </div>
            <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("barometer.solar")}
            </div>
            <div className="mt-5 flex items-end gap-1 h-10">
              {[40, 65, 80, 55, 90, 75, 95, 70, 85, 60, 88, 92].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{
                    height: `${h}%`,
                    background: "var(--gradient-saffron)",
                    opacity: 0.4 + (h / 100) * 0.6,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- ROADMAP ---------------- */
function Roadmap() {
  const { t } = useI18n();
  const ref = useReveal<HTMLDivElement>();
  const milestones = [
    {
      q: t("roadmap.card1.q"),
      title: t("roadmap.card1.title"),
      desc: t("roadmap.card1.desc"),
      done: true,
    },
    {
      q: t("roadmap.card2.q"),
      title: t("roadmap.card2.title"),
      desc: t("roadmap.card2.desc"),
      done: true,
    },
    {
      q: t("roadmap.card3.q"),
      title: t("roadmap.card3.title"),
      desc: t("roadmap.card3.desc"),
      done: true,
    },
    {
      q: t("roadmap.card4.q"),
      title: t("roadmap.card4.title"),
      desc: t("roadmap.card4.desc"),
      done: true,
    },
    {
      q: t("roadmap.card5.q"),
      title: t("roadmap.card5.title"),
      desc: t("roadmap.card5.desc"),
      done: true,
    },
    {
      q: t("roadmap.card6.q"),
      title: t("roadmap.card6.title"),
      desc: t("roadmap.card6.desc"),
      done: false,
    },
  ];

  return (
    <section id="roadmap" className="relative overflow-hidden py-20 sm:py-28 lg:py-32">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(55% 40% at 50% 0%, oklch(0.86 0.16 165 / 0.08), transparent 60%)",
        }}
      />
      <div ref={ref} className="reveal mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-mint">
            // {t("roadmap.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-[clamp(2rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-tight">
            {t("roadmap.title")}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("roadmap.sub")}
          </p>
        </div>

        <div className="relative mt-14 lg:mt-16">
          {/* Desktop horizontal connector */}
          <div
            aria-hidden
            className="absolute left-[calc(16.67%-28px)] right-[calc(16.67%-28px)] top-7 hidden h-px bg-gradient-to-r from-transparent via-white/20 to-transparent lg:block"
          />

          <div className="grid gap-8 md:grid-cols-3 md:gap-6 lg:gap-8">
            {milestones.map((m, i) => (
              <div
                key={m.q}
                className="group relative flex gap-5 md:flex-col md:items-center md:text-center"
              >
                {/* Step indicator */}
                <div className="relative shrink-0">
                  <div
                    className={`grid h-14 w-14 place-items-center rounded-full border-2 transition-colors duration-300 ${
                      m.done
                        ? "border-saffron bg-saffron/10 text-saffron glow-saffron"
                        : "border-white/20 bg-background/80 text-muted-foreground group-hover:border-mint/60 group-hover:text-mint"
                    }`}
                  >
                    {m.done ? (
                      <CheckCircle2 className="h-6 w-6" />
                    ) : (
                      <span className="font-display text-base font-bold">{i + 1}</span>
                    )}
                  </div>
                  {/* Mobile vertical connector */}
                  {i < milestones.length - 1 && (
                    <div
                      aria-hidden
                      className="absolute left-1/2 top-14 h-[calc(100%+1.25rem)] w-px -translate-x-1/2 bg-gradient-to-b from-white/20 to-transparent md:hidden"
                    />
                  )}
                </div>

                {/* Card content */}
                <div className="flex-1 rounded-2xl glass-strong p-5 sm:p-6 md:mt-4 md:bg-transparent md:p-0 md:shadow-none">
                  <div
                    className={`font-mono text-[10px] uppercase tracking-[0.22em] ${
                      m.done ? "text-saffron" : "text-mint"
                    }`}
                  >
                    {m.q}
                  </div>
                  <h3 className="mt-2 font-display text-lg font-semibold tracking-tight sm:text-xl">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                    {m.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- JUPITER SWAP + PARTNER PORTAL ---------------- */
function JupiterSwap() {
  const ref = useReveal<HTMLDivElement>();
  const { t } = useI18n();
  return (
    <section id="swap" className="relative overflow-hidden py-24 sm:py-32">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, oklch(0.97 0.13 120 / 0.10), transparent 60%)",
        }}
      />
      <div
        ref={ref}
        className="reveal mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 md:grid-cols-2 md:items-center"
      >
        <div>
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-saffron">
            {t("jupiter.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
            {t("jupiter.h.a")}<span className="italic text-gradient-saffron">{t("jupiter.h.b")}</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("jupiter.desc")}
          </p>

          <div className="mt-8 space-y-3">
            <button
              type="button"
              className="liquid-btn inline-flex w-full items-center justify-between gap-2 rounded-2xl bg-gradient-to-br from-saffron to-coral px-6 py-4 text-sm font-bold uppercase tracking-wide text-obsidian shadow-[var(--glow-saffron)] sm:w-auto"
            >
              <span className="inline-flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {t("jupiter.partnerLogin")}
              </span>
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <p className="text-xs text-muted-foreground">
              {t("jupiter.partnerNote")}
            </p>
          </div>
        </div>

        {/* Jupiter Terminal placeholder */}
        <div className="relative rounded-3xl glass-strong luminous-border p-5 shadow-[var(--shadow-elev)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="grid h-7 w-7 place-items-center rounded-md bg-gradient-to-br from-saffron to-mint">
                <Sparkle className="h-3.5 w-3.5 text-obsidian" />
              </div>
              <span className="font-display text-sm font-bold tracking-tight">
                Jupiter Terminal
              </span>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mint">
              v3 · Solana
            </span>
          </div>

          <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              You Pay
            </div>
            <div className="mt-2 flex items-center justify-between">
              <input
                defaultValue="250"
                className="w-0 flex-1 bg-transparent font-display text-3xl font-bold outline-none"
              />
              <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs font-bold">
                <span className="h-4 w-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600" />
                USDC
              </span>
            </div>
          </div>
          <div className="my-1.5 flex justify-center">
            <div className="grid h-8 w-8 place-items-center rounded-full glass">
              <ArrowDown className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </div>
          <div className="rounded-2xl border border-saffron/30 bg-saffron/[0.04] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-saffron">
              {t("jupiter.youReceive")}
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="font-display text-3xl font-bold text-gradient-saffron">250.00</div>
              <span className="inline-flex items-center gap-2 rounded-full bg-saffron/10 px-3 py-1.5 text-xs font-bold text-saffron">
                <img src={meal4Coin} alt="" width={16} height={16} loading="lazy" decoding="async" className="h-4 w-4" />
                MEAL4
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            <div className="rounded-lg bg-white/[0.03] px-2 py-1.5 text-center">Slip 0.5%</div>
            <div className="rounded-lg bg-white/[0.03] px-2 py-1.5 text-center">Fee ~$0.001</div>
            <div className="rounded-lg bg-white/[0.03] px-2 py-1.5 text-center">Route 1-hop</div>
          </div>

          <button
            type="button"
            className="liquid-btn mt-4 w-full rounded-2xl bg-gradient-to-br from-saffron to-coral px-6 py-3.5 text-sm font-bold tracking-wide text-obsidian shadow-[var(--glow-saffron)]"
          >
            {t("swap.connectWallet")}
          </button>
          <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t("swap.jupiter")}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHITEPAPER & LEGAL DOCUMENTS ---------------- */
function WhitepaperDocs() {
  const { t } = useI18n();
  const ref = useReveal<HTMLDivElement>();
  const [openDoc, setOpenDoc] = useState<DocId | null>(null);
  const docs = [
    {
      label: t("docs.btn1"),
      icon: FileText,
      lang: "EN",
      href: "/docs/SFN_Network_Master_Whitepaper_v1.0.pdf",
      file: "SFN_Network_Master_Whitepaper_v1.0.pdf",
      doc: null as DocId | null,
    },
    {
      label: t("docs.btn2"),
      icon: Download,
      lang: "DE / EN",
      href: "/docs/executive-tokenomics-summary-de.pdf",
      file: "executive-tokenomics-summary-de.pdf",
      doc: "tokenomics" as DocId | null,
    },
    {
      label: t("docs.btn3"),
      icon: ShieldCheck,
      lang: "DE / EN",
      href: "/docs/thailand-gastronomy-legal-framework.pdf",
      file: "thailand-gastronomy-legal-framework.pdf",
      doc: "legal" as DocId | null,
    },
    {
      label: t("docs.btn4"),
      icon: Lock,
      lang: "DE / EN",
      href: "/docs/cryptographic-security-audit-report.pdf",
      file: "cryptographic-security-audit-report.pdf",
      doc: "audit" as DocId | null,
    },
  ];

  return (
    <section id="docs" className="relative overflow-hidden py-20 sm:py-28 lg:py-32">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(55% 40% at 50% 100%, oklch(0.82 0.18 75 / 0.08), transparent 60%)",
        }}
      />
      <div ref={ref} className="reveal mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-saffron">
            // {t("docs.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-[clamp(2rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-tight">
            {t("docs.title")}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("docs.sub")}
          </p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-saffron">
            {t("credits.line")}
          </p>
        </div>

        <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center gap-2 rounded-2xl border border-saffron/25 bg-saffron/[0.06] px-5 py-4 text-center">
          <span className="rounded-full border border-saffron/40 bg-saffron/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-saffron">
            {t("credits.version")}
          </span>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-foreground">
            {AUTHOR_LINE}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            {t("credits.date")}
          </p>
        </div>


        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:gap-5">
          {docs.map((d) => {
            const cls =
              "group flex w-full items-center gap-4 rounded-2xl glass luminous-border p-4 text-left transition-all duration-300 hover:shadow-[var(--shadow-elev)] hover:-translate-y-0.5 sm:p-5";
            const inner = (
              <>
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-saffron/20 to-coral/20 text-saffron transition-colors group-hover:from-saffron/30 group-hover:to-coral/30">
                  <d.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold tracking-tight text-foreground transition-colors group-hover:text-saffron sm:text-[15px]">
                    {d.label}
                  </span>
                  <span className="mt-0.5 block text-[10px] text-muted-foreground uppercase tracking-wider">
                    {d.lang} · {d.doc ? "READ / PDF" : "PDF"}
                  </span>
                </div>
                <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </>
            );
            return d.doc ? (
              <button
                key={d.label}
                type="button"
                onClick={() => {
                  track("doc_open", { file: d.file, lang: d.lang });
                  setOpenDoc(d.doc);
                }}
                aria-label={`${d.label} — open reading view`}
                className={cls}
              >
                {inner}
              </button>
            ) : (
              <a
                key={d.label}
                href={d.href}
                download={d.file}
                rel="noopener"
                onClick={() => track("doc_download", { file: d.file, lang: d.lang })}
                aria-label={`${d.label} — download PDF`}
                className={cls}
              >
                {inner}
              </a>
            );
          })}
        </div>
      </div>
      {openDoc && <LegalDocModal docId={openDoc} onClose={() => setOpenDoc(null)} />}
    </section>
  );
}

/* ---------------- HOW WE KEEP MEALS AT $1 ---------------- */
function KeepAtOneDollar() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  const cards = [
    {
      icon: Sun,
      tone: "text-saffron",
      glow: "oklch(0.82 0.18 75 / 0.30)",
      title: t("keep.solar.title"),
      text: t("keep.solar.text"),
    },
    {
      icon: Sprout,
      tone: "text-mint",
      glow: "oklch(0.86 0.16 165 / 0.30)",
      title: t("keep.agri.title"),
      text: t("keep.agri.text"),
    },
    {
      icon: Droplets,
      tone: "text-mint",
      glow: "oklch(0.8 0.14 230 / 0.30)",
      title: t("keep.water.title"),
      text: t("keep.water.text"),
    },
  ];
  return (
    <section id="how" className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div ref={r} className="reveal mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl md:text-5xl">
            {t("keep.title")}
          </h2>
          <p className="mt-4 text-base text-muted-foreground">{t("keep.sub")}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-14 md:grid-cols-3">
          {cards.map((c) => (
            <div
              key={c.title}
              className="relative overflow-hidden rounded-3xl glass p-6 sm:p-7"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full blur-3xl"
                style={{ background: c.glow }}
              />
              <div className="relative z-10">
                <c.icon className={cn("h-6 w-6", c.tone)} aria-hidden />
                <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">
                  {c.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Sticky mobile conversion bar — appears once the hero fold scrolls away. */
function StickyMobileCta() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 640);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      data-testid="sticky-mobile-cta"
      aria-hidden={!visible}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-background/85 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl transition-transform duration-300 lg:hidden",
        visible ? "translate-y-0" : "pointer-events-none translate-y-full",
      )}
    >
      <div className="grid grid-cols-2 gap-2">
        <a
          href="#secure"
          onClick={() => track("cta_click", { id: "sticky_buy_meal2", surface: "sticky_mobile" })}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-saffron to-coral px-4 text-[13px] font-bold text-obsidian"
        >
          {t("hero.cta")}
        </a>
        <a
          href="#tokens"
          onClick={() => track("cta_click", { id: "sticky_get_meal4", surface: "sticky_mobile" })}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl glass px-4 text-[13px] font-medium text-foreground"
        >
          {t("hero.ghost")}
        </a>
      </div>
    </div>
  );
}

function Page() {
  return (

    <div id="top" className="relative min-h-screen bg-background text-foreground overflow-x-hidden">
      <Nav />
      <main>
        {/* Fold 1 — $1 value proposition + buy widget */}
        <Hero />


        {/* Fold 2 — Problem (food inflation) vs. solution ($1 price freeze) */}
        <Barometer />
        <ProtectionSection />

        {/* Fold 3 — Live pilot proof */}
        <PilotTrustBanner />
        <PhaseOneCard />
        <KeepAtOneDollar />

        {/* Fold 4 — Dual-token buy section */}
        <DualToken />
        <Tokenomics />
        <NextMilestones />
        <Roadmap />
        <Bento />

        {/* Fold 5 — Audit, trust & legal */}
        <Trust />
        <Transparency />
        <VerifyReceiptSection />
        <WhyOnChain />
        <About />
        <FAQ />
        <WhitepaperDocs />
        <Contact />
      </main>
      <Footer />
      <StickyMobileCta />
      <AnalyticsConsentBar />

    </div>
  );
}


export default function MealTokenLanding() {
  return (
    <I18nProvider>
      <EcosystemAccentProvider>
        <Page />
      </EcosystemAccentProvider>
    </I18nProvider>
  );
}
